import { DOC_LIMITATION, validateDocumentContents } from "./dashboard/documentFiles";
import { CloseIcon } from "./dashboard/shared";
import { NotificationBell, NotificationsPage, useNotifications } from "./dashboard/Notifications";
import ClassroomInformation, { ClassCode } from "./dashboard/ClassroomInformation";
import ValidatedForm, { ValidatedInput, ValidatedTextarea, ValidatedSelect } from "../ValidatedForm";
import { compareStudentsByLastName } from "./dashboard/rosterSort";
import SimilarityPreview from "./dashboard/SimilarityPreview";
import SubmissionReview from "./dashboard/SubmissionReview";
import DeleteAssignmentDialog from "./dashboard/DeleteAssignmentDialog";
import EssayPagePicker from "./dashboard/EssayPagePicker";
import { combinePageText, isEssayImage, validateEssayPages, useUploadLimits } from "./dashboard/essayPages";
import { PeerComparison, SimilarityDialog } from "./dashboard/PeerSimilarityReview";
import { PaginatedList } from "./dashboard/Pagination";
import ClassmateMatches from "./dashboard/ClassmateMatches";
import AssignmentCalendar from "./dashboard/AssignmentCalendar";
import RevisionHistory from "./dashboard/RevisionHistory";
import AssignmentComments from "./dashboard/AssignmentComments";
import { useClassroomArchiveSync } from "./dashboard/useClassroomArchiveSync";
import SubmissionStation from "./dashboard/SubmissionStation";
import SubmissionFilePreview from "./dashboard/SubmissionFilePreview";
import { downloadGrades, loadGradeExport } from "./dashboard/gradeExport";
import { processingLabel, useSubmissionProgress, loadTeacherSubmissionStatus } from "./dashboard/submissionProgress";
import { apiFetch, getBackendUrl } from "../apiFetch";
import ClassroomDetail from "./dashboard/ClassroomDetail";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { supabase } from "../supabaseClient";
import {
  ASSIGNMENT_TABLE,
  CLASSROOM_TABLE,
  MEMBER_TABLE,
  ClipboardIcon,
  ESSAY_BUCKET,
  resolveStorageImageUrl,
  DoorIcon,
  FileIcon,
  FileSearchIcon,
  Header,
  ImageIcon,
  PlusIcon,
  StatusMessage,
  UploadIcon,
  UsersIcon,
  SparklesIcon,
  CheckIcon,
  CopyIcon,
  ChevronDownIcon,
  buildClassCode,
  emptyAssignmentForm,
  emptyClassroomForm,
  formatDateTime,
  normalizeAssignment,
  normalizeClassroom,
  downloadSubmissionFileBlob,
  openSubmissionFile,
  teacherPages,
  EditIcon,
  toDateTimeLocalInput,
  ArrowLeftIcon,
  SearchIcon,
  ExternalLinkIcon,
  DownloadIcon,
  getInitials,
  getTeacherAvatarTheme,
  isCustomAvatarUrl,
  ArchiveIcon,
  UnarchiveIcon,
  getAvatarPublicUrl,
} from "./dashboard/shared";
import HighlightedText from "./HighlightedText";
import {
  ACCEPTED_CHECK_FILE_TYPES,
  checkPlagiarismViaBackend,
  fetchUserPlagiarismScans,
  formatFileSize,
  getFileKind,
  pollPlagiarismScanResult,
  readTextFromFiles,
} from "./dashboard/plagiarismScan";
import {
  extractTextFromImages,
  getOcrEngineInfo,
} from "./dashboard/ocrService";

function resolveBackendUrl() {
  if (typeof getBackendUrl === "function") {
    try {
      const u = getBackendUrl();
      if (u) return u;
    } catch {}
  }
  return process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
}

const uploadModes = [
  {
    id: "picture",
    label: "Picture",
    icon: ImageIcon,
  },
  {
    id: "file",
    label: "File",
    icon: FileIcon,
  },
  {
    id: "text",
    label: "Paste",
    icon: ClipboardIcon,
  },
];

function getTeacherActivePage(profileId) {
  if (!profileId || typeof window === "undefined") return "classrooms";

  const savedPage = window.sessionStorage.getItem(
    `writecheck-teacher-active-page-${profileId}`
  );

  return teacherPages.some((page) => page.id === savedPage)
    ? savedPage
    : "classrooms";
}

export default function TeacherDashboard({ profile, onProfileUpdated }) {
  const notifications = useNotifications(profile?.id);
  const [commentTarget, setCommentTarget] = useState(null);
  const manualSelection = useRef(0);
  const uploadLimits = useUploadLimits();
  const [openedClassroomId, setOpenedClassroomId] = useState(null);

  const [activePage, setActivePage] =
    useState(() => getTeacherActivePage(profile?.id));

  const [classrooms, setClassrooms] =
    useState([]);
  const [classroomTab, setClassroomTabState] = useState(() => {
    if (typeof window !== "undefined") {
      const urlTab = new URLSearchParams(window.location.search).get("tab");
      if (urlTab === "active" || urlTab === "archived") return urlTab;
      try {
        const saved = sessionStorage.getItem(`writecheck-classroom-tab-${profile?.id || "teacher"}`);
        if (saved === "active" || saved === "archived") return saved;
      } catch {}
    }
    return "active";
  });

  const setClassroomTab = useCallback((tab) => {
    setClassroomTabState(tab);
    if (typeof window !== "undefined") {
      try {
        sessionStorage.setItem(`writecheck-classroom-tab-${profile?.id || "teacher"}`, tab);
        const url = new URL(window.location.href);
        url.searchParams.set("tab", tab);
        window.history.replaceState({}, "", url.toString());
      } catch {}
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [profile?.id]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      if ("scrollRestoration" in window.history) {
        window.history.scrollRestoration = "manual";
      }
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }
  }, [classroomTab, activePage]);
  const [archivingClassroom, setArchivingClassroom] = useState(null);
  const [isArchiving, setIsArchiving] = useState(false);
  const [copyingClassroom, setCopyingClassroom] = useState(null);
  const [copyForm, setCopyForm] = useState({ name: "", section: "", subject: "" });
  const [isCopying, setIsCopying] = useState(false);

  const [loadedAssignments, setAssignments] =
    useState([]);
  const assignments = useMemo(() => loadedAssignments.map(assignment => ({ ...assignment,
    classroomName: classrooms.find(c => c.id === assignment.classroomId)?.name || assignment.classroomName,
  })), [loadedAssignments, classrooms]);

  const [submissions, setSubmissions] =
    useState([]);

  useEffect(() => {
    setSubmissions(current => {
      let changed = false;
      const next = current.map(item => {
        const name = classrooms.find(c => String(c.id) === String(item.classroomId))?.name;
        if (!name || name === item.classroomName) return item;
        changed = true;
        return { ...item, classroomName: name };
      });
      return changed ? next : current;
    });
  }, [classrooms]);

  const [classroomForm, setClassroomForm] =
    useState(emptyClassroomForm);

  const [assignmentForm, setAssignmentForm] =
    useState(emptyAssignmentForm);

  const [editingAssignment, setEditingAssignment] = useState(null);
  const [deletingAssignment, setDeletingAssignment] = useState(null);
  const [isUpdatingAssignment, setIsUpdatingAssignment] = useState(false);
  const [isCreatingAssignment, setIsCreatingAssignment] = useState(false);
  const [assignmentDetailTab, setAssignmentDetailTab] = useState("roster");
  const teacherDataRequestRef = useRef(0);
  const teacherDataLoadedOnce = useRef(false);

  const [classroomMembers, setClassroomMembers] = useState([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState(null);
  const [peerInspection, setPeerInspection] = useState(null);
  const [assignmentFilterClassroomId, setAssignmentFilterClassroomId] = useState("all");
  const [submissionRosterFilter, setSubmissionRosterFilter] = useState("all");
  const [submissionRosterSearch, setSubmissionRosterSearch] = useState("");
  const [selectedSubmissionsClassroomId, setSelectedSubmissionsClassroomId] = useState("all");
  const [exportingGrades, setExportingGrades] = useState(false);
  const [linkedScanAssignmentId, setLinkedScanAssignmentId] = useState("all");
  const [loadedSubmissionInfo, setLoadedSubmissionInfo] = useState(null);
  const [loadingSubmissionId, setLoadingSubmissionId] = useState(null);

  const [uploadMode, setUploadMode] =
    useState("");

  const [manualCheckTitle, setManualCheckTitle] =
    useState("");

  const [manualCheckText, setManualCheckText] =
    useState("");

  const [manualCheckFiles, setManualCheckFiles] =
    useState([]);

  const [manualImagePreview, setManualImagePreview] =
    useState("");

  const [manualCheckResult, setManualCheckResult] =
    useState(null);

  const [plagiarismScanProgressText, setPlagiarismScanProgressText] =
    useState("");

  const [reviewingSubmission, setReviewingSubmission] =
    useState(null);

  const [gradeInput, setGradeInput] =
    useState("");

  const [feedbackInput, setFeedbackInput] =
    useState("");

  const [isSavingGrade, setIsSavingGrade] =
    useState(false);

  const [reviewScanResult, setReviewScanResult] =
    useState(null);

  const [reviewTranscribedText, setReviewTranscribedText] =
    useState("");

  const [isReviewScanning, setIsReviewScanning] =
    useState(false);

  const [reviewScanProgressText, setReviewScanProgressText] =
    useState("");

  const [reviewCopySuccess, setReviewCopySuccess] =
    useState(false);

  const [reviewImagePreviewUrl, setReviewImagePreviewUrl] =
    useState("");

  const [isLoadingImagePreview, setIsLoadingImagePreview] =
    useState(false);

  const [isImageExpanded, setIsImageExpanded] =
    useState(false);

  const [isManualImageExpanded, setIsManualImageExpanded] =
    useState(false);

  const [manualLiveOcrResult, setManualLiveOcrResult] =
    useState(null);

  const [ocrEngineInfo, setOcrEngineInfo] =
    useState(null);

  const [isTranscribing, setIsTranscribing] =
    useState(false);

  const [transcribedText, setTranscribedText] =
    useState("");

  const [transcriptionResult, setTranscriptionResult] =
    useState(null);

  const [showLineBreakdown, setShowLineBreakdown] =
    useState(false);

  const [copySuccess, setCopySuccess] =
    useState(false);

  const [isEditingTranscript, setIsEditingTranscript] =
    useState(false);

  const [manualCheckError, setManualCheckError] =
    useState("");

  const manualCheckImageUrl = useMemo(() => {
    if (manualImagePreview) return manualImagePreview;
    if (manualCheckResult?.imageUrl) return manualCheckResult.imageUrl;

    const candidateFile = manualCheckFiles?.find((f) => f.type?.startsWith("image/"));
    if (candidateFile) {
      try {
        return URL.createObjectURL(candidateFile);
      } catch {
        // ignore
      }
    }

    const candidateName =
      manualCheckFiles?.[0]?.name ||
      manualCheckResult?.imageName ||
      manualCheckResult?.extractedImages?.[0]?.fileName ||
      (manualCheckTitle && /\.(jpe?g|png|webp|gif)$/i.test(manualCheckTitle) ? manualCheckTitle : null);

    if (candidateName && /\.(jpe?g|png|webp|gif)$/i.test(candidateName)) {
      const backendUrl = resolveBackendUrl();
      return `${backendUrl}/uploads/${candidateName}`;
    }

    return "";
  }, [manualImagePreview, manualCheckResult, manualCheckFiles, manualCheckTitle]);

  const [isCreatingClassroom, setIsCreatingClassroom] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(true);

  const [isSavingClassroom, setIsSavingClassroom] =
    useState(false);

  const [isSavingAssignment, setIsSavingAssignment] =
    useState(false);

  const [isScanningManualCheck, setIsScanningManualCheck] =
    useState(false);

  const [errorMessage, setErrorMessage] =
    useState("");

  const [successMessage, setSuccessMessage] =
    useState("");

  useEffect(() => {
    if (!successMessage && !errorMessage) return undefined;

    const timeoutId = window.setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => window.clearTimeout(timeoutId);
  }, [successMessage, errorMessage]);

  useEffect(() => {
    setSuccessMessage("");
    setErrorMessage("");
  }, [activePage]);

  useEffect(() => {
    if (!profile?.id || typeof window === "undefined") return;

    window.sessionStorage.setItem(
      `writecheck-teacher-active-page-${profile.id}`,
      activePage
    );
  }, [activePage, profile?.id]);

  const activeClassrooms = useMemo(
    () => classrooms.filter((classroom) => !classroom.isArchived),
    [classrooms]
  );
  const archivedClassrooms = useMemo(
    () => classrooms.filter((classroom) => Boolean(classroom.isArchived)),
    [classrooms]
  );
  useClassroomArchiveSync(classrooms, setClassrooms);

  const visibleClassrooms = classroomTab === "active" ? activeClassrooms : archivedClassrooms;

  const selectedAssignment = useMemo(() => {
    return assignments.find((a) => a.id === selectedAssignmentId) || null;
  }, [assignments, selectedAssignmentId]);

  const assignmentRoster = useMemo(() => {
    if (!selectedAssignment) return [];

    const enrolledMembers = classroomMembers.filter(
      (m) => m.classroomId === selectedAssignment.classroomId
    );

    const assignmentSubmissions = submissions.filter(
      (s) =>
        s.assignmentId === selectedAssignment.id &&
        s.classroomId === selectedAssignment.classroomId
    );

    const submissionByStudentId = new Map(
      assignmentSubmissions.map((s) => [s.studentId, s])
    );

    const enrolledStudentIds = new Set(enrolledMembers.map((m) => m.studentId));
    const roster = enrolledMembers.map((member) => {
      const sub = submissionByStudentId.get(member.studentId);
      const isSubmitted = Boolean(sub);
      const isGraded = sub?.grade != null && String(sub.grade).trim() !== "";

      let status = "missing";
      if (sub?.revisionRequest) {
        status = "revision";
      } else if (isGraded) {
        status = "graded";
      } else if (isSubmitted) {
        if (
          selectedAssignment.dueDate &&
          new Date(sub.createdAt) > new Date(selectedAssignment.dueDate)
        ) {
          status = "late";
        } else {
          status = "submitted";
        }
      }

      return {
        studentId: member.studentId,
        studentName: member.studentName || "Student",
        studentEmail: member.studentEmail || "",
        submission: sub || null,
        isSubmitted,
        status,
        fileUrl: sub?.fileUrl || null,
        essayTitle: sub?.essayTitle || "",
        grade: sub?.grade ?? "",
        feedback: sub?.feedback || "",
        submittedAt: sub?.createdAt || null,
      };
    });

    for (const sub of assignmentSubmissions) {
      if (!enrolledStudentIds.has(sub.studentId)) {
        const isGraded = Boolean(sub.grade);
        let status = "missing";
        if (sub.revisionRequest) {
          status = "revision";
        } else if (isGraded) {
          status = "graded";
        } else {
          if (
            selectedAssignment.dueDate &&
            new Date(sub.createdAt) > new Date(selectedAssignment.dueDate)
          ) {
            status = "late";
          } else {
            status = "submitted";
          }
        }
        roster.push({
          studentId: sub.studentId,
          studentName: sub.studentName || "Student",
          studentEmail: "",
          submission: sub,
          isSubmitted: true,
          status,
          fileUrl: sub.fileUrl || null,
          essayTitle: sub.essayTitle || "",
          grade: sub.grade ?? "",
          feedback: sub.feedback || "",
          submittedAt: sub.createdAt || null,
        });
      }
    }

    return roster.sort(compareStudentsByLastName);
  }, [selectedAssignment, classroomMembers, submissions]);

  const filteredAssignmentRoster = useMemo(() => {
    let list = assignmentRoster;

    if (submissionRosterFilter === "submitted") {
      list = list.filter((item) => item.isSubmitted);
    } else if (submissionRosterFilter === "graded") {
      list = list.filter((item) => Boolean(item.grade));
    } else if (submissionRosterFilter === "missing") {
      list = list.filter((item) => !item.isSubmitted);
    }

    const term = submissionRosterSearch.trim().toLowerCase();
    if (term) {
      list = list.filter(
        (item) =>
          item.studentName.toLowerCase().includes(term) ||
          item.studentEmail.toLowerCase().includes(term) ||
          item.essayTitle.toLowerCase().includes(term)
      );
    }

    return list;
  }, [assignmentRoster, submissionRosterFilter, submissionRosterSearch]);

  const assignmentStats = useMemo(() => {
    const total = assignmentRoster.length;
    const submitted = assignmentRoster.filter((r) => r.isSubmitted).length;
    const graded = assignmentRoster.filter((r) => Boolean(r.grade)).length;
    const missing = total - submitted;

    return {
      total,
      submitted,
      graded,
      missing,
    };
  }, [assignmentRoster]);

  const filteredAssignments = useMemo(() => {
    if (assignmentFilterClassroomId === "all") return assignments;
    return assignments.filter((a) => a.classroomId === assignmentFilterClassroomId);
  }, [assignments, assignmentFilterClassroomId]);

  const gradeClasswork = assignments.filter(assignment => selectedSubmissionsClassroomId === "all"
    || String(assignment.classroomId) === String(selectedSubmissionsClassroomId));

  // Leaving assignment detail clears its scan link, without touching scan content or the classroom.
  function clearAssignmentContext() {
    setSelectedAssignmentId(null);
    setCommentTarget(null);
    setLinkedScanAssignmentId("all");
  }

  function selectGradeAssignment(assignmentId) {
    setErrorMessage("");
    setSuccessMessage("");
    const assignment = assignments.find(row => String(row.id) === String(assignmentId));
    if (assignment) setSelectedAssignmentId(assignment.id);
    else clearAssignmentContext();
    setAssignmentDetailTab("roster");
    setSubmissionRosterFilter("all");
    setSubmissionRosterSearch("");
    if (assignment) {
      setSelectedSubmissionsClassroomId(assignment.classroomId);
      setOpenedClassroomId(assignment.classroomId);
      setAssignmentFilterClassroomId(assignment.classroomId);
    }
  }

  const [gradesOrigin, setGradesOrigin] = useState(null);

  function selectGradeClassroom(classroomId) {
    setSelectedSubmissionsClassroomId(classroomId);
    setOpenedClassroomId(classroomId === "all" ? null : classroomId);
    setAssignmentFilterClassroomId(classroomId);
    selectGradeAssignment(null);
  }

  async function handleExportGrades(context = {}) {
    if (exportingGrades) return;
    setExportingGrades(true);
    setErrorMessage("");
    try {
      const { rows, classroomId, assignmentTitle } = await loadGradeExport(supabase, context);
      if (!rows.length) { setErrorMessage("No student grade records in this selection."); return; }
      const classroomName = classrooms.find(c => String(c.id) === String(classroomId))?.name || String(classroomId);
      await downloadGrades(rows, profile.full_name || "Teacher", [classroomName, assignmentTitle].filter(Boolean).join(" / "));
      setSuccessMessage(`Exported ${rows.length} student assignment records to Excel.`);
    } catch (error) {
      setErrorMessage(error.message || "Could not export grades. Please try again.");
    } finally { setExportingGrades(false); }
  }

  const submissionHubGroups = useMemo(() => {

    const enriched = assignments
      .map((assignment) => {
        // Counts are intentionally computed from this assignment's own classroom.
        // This keeps submissions and missing students isolated by section.
        const enrolledStudentIds = new Set(
          classroomMembers
            .filter((member) => member.classroomId === assignment.classroomId)
            .map((member) => member.studentId)
        );
        const assignmentSubmissions = submissions.filter(
          (submission) =>
            submission.assignmentId === assignment.id &&
            submission.classroomId === assignment.classroomId &&
            enrolledStudentIds.has(submission.studentId)
        );
        const submittedStudentIds = new Set(
          assignmentSubmissions.map((submission) => submission.studentId)
        );
        const latestSubmissionAt = assignmentSubmissions.reduce((latest, submission) => {
          const submittedAt = new Date(submission.createdAt || 0).getTime();
          return Math.max(latest, Number.isNaN(submittedAt) ? 0 : submittedAt);
        }, 0);

        return {
          ...assignment,
          submissionStats: {
            submitted: submittedStudentIds.size,
            missing: Math.max(0, enrolledStudentIds.size - submittedStudentIds.size),
            latestSubmissionAt,
          },
        };
      })
      .filter((assignment) => {
        if (activePage === "submissions") {
          return selectedSubmissionsClassroomId === "all" || String(assignment.classroomId) === String(selectedSubmissionsClassroomId);
        }
        return true;
      })
      .sort((a, b) => {
        return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
      });

    return enriched.reduce((groups, assignment) => {
      const group = groups.get(assignment.classroomId) || {
        id: assignment.classroomId,
        name: assignment.classroomName,
        section: assignment.classroomSection,
        subject: assignment.classroomSubject,
        assignments: [],
      };
      group.assignments.push(assignment);
      groups.set(assignment.classroomId, group);
      return groups;
    }, new Map());
  }, [
    assignments,
    classroomMembers,
    submissions,
    activePage,
    selectedSubmissionsClassroomId,
  ]);

  const selectedScanAssignment = useMemo(() => {
    if (!linkedScanAssignmentId || linkedScanAssignmentId === "all") {
      return null;
    }
    return assignments.find((a) => String(a.id) === String(linkedScanAssignmentId)) || null;
  }, [assignments, linkedScanAssignmentId]);

  const selectedScanRoster = useMemo(() => {
    if (!selectedScanAssignment) return [];
    const assignmentId = String(selectedScanAssignment.id);
    const classroomId = String(selectedScanAssignment.classroomId);

    const enrolledMembers = classroomMembers.filter(
      (member) => String(member.classroomId) === classroomId
    );

    const assignmentSubmissions = submissions.filter(
      (sub) =>
        String(sub.assignmentId || sub.assignment_id) === assignmentId &&
        String(sub.classroomId || sub.classroom_id) === classroomId
    );

    const submissionByStudentId = new Map(
      assignmentSubmissions.map((s) => [String(s.studentId || s.student_id), s])
    );

    const enrolledIds = new Set(enrolledMembers.map((m) => String(m.studentId)));

    const roster = enrolledMembers.map((member) => {
      const sub = submissionByStudentId.get(String(member.studentId));
      const isSubmitted = Boolean(sub);
      const isGraded = sub?.grade != null && String(sub.grade).trim() !== "";

      let status = "missing";
      if (isGraded) {
        status = "graded";
      } else if (isSubmitted) {
        if (
          selectedScanAssignment.dueDate &&
          new Date(sub.createdAt) > new Date(selectedScanAssignment.dueDate)
        ) {
          status = "late";
        } else {
          status = "submitted";
        }
      }

      return {
        studentId: member.studentId,
        studentName: member.studentName || "Student",
        studentEmail: member.studentEmail || "",
        submission: sub || null,
        isSubmitted,
        isGraded,
        status,
        fileUrl: sub?.fileUrl || null,
        essayTitle: sub?.essayTitle || "",
        grade: sub?.grade ?? "",
        feedback: sub?.feedback || "",
        submittedAt: sub?.createdAt || null,
        scanResult: sub?.scanResult || null,
        transcribedText: sub?.transcribedText || "",
      };
    });

    for (const sub of assignmentSubmissions) {
      const sId = String(sub.studentId || sub.student_id);
      if (!enrolledIds.has(sId)) {
        const isGraded = sub?.grade != null && String(sub.grade).trim() !== "";
        roster.push({
          studentId: sId,
          studentName: sub.studentName || "Student",
          studentEmail: "",
          submission: sub,
          isSubmitted: true,
          isGraded,
          status: isGraded ? "graded" : "submitted",
          fileUrl: sub?.fileUrl || null,
          essayTitle: sub?.essayTitle || "",
          grade: sub?.grade ?? "",
          feedback: sub?.feedback || "",
          submittedAt: sub?.createdAt || null,
          scanResult: sub?.scanResult || null,
          transcribedText: sub?.transcribedText || "",
        });
      }
    }

    return roster.sort(compareStudentsByLastName);
  }, [selectedScanAssignment, classroomMembers, submissions]);

  const submissionSyncError = useSubmissionProgress(profile?.id, setSubmissions, true, { assignments, members: classroomMembers });

  const loadTeacherData = useCallback(async (silent = false) => {
    const teacherId = profile?.id;
    if (!teacherId) return false;

    const requestId = teacherDataRequestRef.current + 1;
    teacherDataRequestRef.current = requestId;
    if (!silent && !teacherDataLoadedOnce.current) {
      setIsLoading(true);
      setErrorMessage("");
    }

    let classroomRows = [];
    let classroomError = null;

    const resWithArchived = await supabase
      .from(CLASSROOM_TABLE)
      .select("id, created_at, teacher_id, classroom_name, classroom_code, subject, section, teacher_name, is_archived")
      .eq("teacher_id", teacherId)
      .order("created_at", { ascending: false });

    if (resWithArchived.error) {
      const resFallback = await supabase
        .from(CLASSROOM_TABLE)
        .select("id, created_at, teacher_id, classroom_name, classroom_code, subject, section, teacher_name")
        .eq("teacher_id", teacherId)
        .order("created_at", { ascending: false });
      classroomRows = resFallback.data;
      classroomError = resFallback.error;
    } else {
      classroomRows = resWithArchived.data;
      classroomError = resWithArchived.error;
    }

    if (classroomError) {
      setErrorMessage(classroomError.message);
      setIsLoading(false);
      return false;
    }

    if (requestId !== teacherDataRequestRef.current) return false;

    const cachedArchivedSet = new Set();
    try {
      const storedKeys = [
        `writecheck_archived_classes_${teacherId}`,
        "writecheck_archived_classes_teacher",
      ];
      storedKeys.forEach((key) => {
        const stored = JSON.parse(localStorage.getItem(key) || "[]");
        if (Array.isArray(stored)) {
          stored.forEach((id) => cachedArchivedSet.add(String(id)));
        }
      });
    } catch {}

    // Query backend for archived classrooms (account-scoped database check)
    let backendArchivedIds = null;
    if (process.env.NODE_ENV !== "test") {
      try {
        const backendUrl = typeof getBackendUrl === "function" ? getBackendUrl() : (process.env.REACT_APP_BACKEND_URL || "http://localhost:8000");
        const archResp = await apiFetch(`${backendUrl}/api/classrooms/archived`);
        if (archResp && archResp.ok) {
          const archData = await archResp.json();
          if (archData?.success && Array.isArray(archData?.archived_ids)) {
            backendArchivedIds = new Set(archData.archived_ids.map(String));
          }
        }
      } catch {}
    }

    if (profile?.id) {
      try {
        localStorage.setItem(`writecheck_teacher_${profile.id}`, JSON.stringify({
          id: profile.id,
          name: profile.full_name || profile.email || "Teacher",
          email: profile.email || "",
          avatarUrl: profile.avatarUrl || "",
        }));
      } catch {}

      const missingTeacherName = (classroomRows ?? []).filter(
        (c) => c.teacher_id === profile.id && (!c.teacher_name || c.teacher_name !== profile.full_name)
      );
      if (missingTeacherName.length > 0 && profile.full_name) {
        supabase
          .from(CLASSROOM_TABLE)
          .update({ teacher_name: profile.full_name })
          .in("id", missingTeacherName.map((c) => c.id))
          .then(() => {});
      }
    }

    const classIds =
      (classroomRows ?? []).map((classroom) => classroom.id);

    let memberRows = [];
    let assignmentRows = [];
    let submissionRows = [];
    let submissionStates = {};
    let studentRows = [];
    const rosterNames = new Map();

    const assignmentRequest = supabase
        .from(ASSIGNMENT_TABLE)
        .select("id, created_at, classroom_id, teacher_id, title, instructions, due_date, accept_late_submissions")
        .eq("teacher_id", teacherId)
        .order("created_at", { ascending: false }).then((response) => response, (error) => ({ data: null, error }));

    if (classIds.length > 0) {
      const rosters = await Promise.all(classIds.map(async (classroomId) => {
        const { data, error } = await supabase.rpc("get_classroom_roster", {
          requested_classroom_id: String(classroomId),
        });
        return { classroomId, data, error };
      }));
      if (requestId !== teacherDataRequestRef.current) return false;
      const rosterError = rosters.find((roster) => roster.error)?.error;
      if (rosterError) {
        setErrorMessage(`Could not load enrolled student names: ${rosterError.message}`);
        setIsLoading(false);
        return false;
      }
      memberRows = rosters.flatMap(({ classroomId, data }) => (data ?? []).map((student) => {
        rosterNames.set(student.student_id, student.student_name);
        return {
          id: `${classroomId}:${student.student_id}`,
          classroom_id: classroomId,
          student_id: student.student_id,
        };
      }));
    }

    let { data: assignmentsData, error: assignmentError } = await assignmentRequest;

    if (assignmentError && (assignmentError.message?.includes("accept_late_submissions") || assignmentError.code === "42703" || assignmentError.code === "PGRST204")) {
      const fallback = await supabase
        .from(ASSIGNMENT_TABLE)
        .select("id, created_at, classroom_id, teacher_id, title, instructions, due_date")
        .eq("teacher_id", teacherId)
        .order("created_at", { ascending: false });
      assignmentsData = fallback.data;
      assignmentError = fallback.error;
    }

    if (assignmentError) {
      setErrorMessage(assignmentError.message);
      setIsLoading(false);
      return false;
    }

    assignmentRows =
      assignmentsData ?? [];

    const assignmentIds =
      assignmentRows.map((assignment) => assignment.id);

    if (assignmentIds.length > 0) {
      try {
        const status = await loadTeacherSubmissionStatus();
        submissionRows = status.results;
        submissionStates = status.progress;
      } catch (submissionError) {
        setErrorMessage(submissionError.message || "Unable to verify API results.");
        setIsLoading(false);
        return false;
      }
    }

    const studentIdsFromMembers = memberRows.map((member) => member.student_id).filter(Boolean);
    const studentIdsFromSubmissions = submissionRows.map((submission) => submission.student_id).filter(Boolean);
    const studentIds =
      [...new Set([...studentIdsFromMembers, ...studentIdsFromSubmissions])];

    if (studentIds.length > 0) {
      const { data: users } =
        await supabase
          .from("userTable")
          .select("id, full_name, email")
          .in("id", studentIds);

      studentRows =
        users ?? [];
    }

    const memberCountByClass =
      memberRows.reduce((counts, member) => {
        counts[member.classroom_id] =
          (counts[member.classroom_id] ?? 0) + 1;
        return counts;
      }, {});

    const assignmentCountByClass =
      assignmentRows.reduce((counts, assignment) => {
        counts[assignment.classroom_id] =
          (counts[assignment.classroom_id] ?? 0) + 1;
        return counts;
      }, {});

    const submissionCountByClass =
      submissionRows.reduce((counts, submission) => {
        counts[submission.classroom_id] =
          (counts[submission.classroom_id] ?? 0) + 1;
        return counts;
      }, {});

    const nextClassrooms =
      (classroomRows ?? []).map((classroom, index) => {
        const classIdStr = String(classroom.id);
        let isArchived = false;

        // Authoritative resolution:
        // 1. Account-scoped check from backend database query:
        if (backendArchivedIds !== null) {
          isArchived = backendArchivedIds.has(classIdStr);
        }
        // 2. Direct database column value if returned by Supabase query:
        else if (typeof classroom.is_archived === "boolean") {
          isArchived = classroom.is_archived;
        }
        // 3. No confirmed archive record: do not infer it from browser storage:
        else {
          isArchived = false;
        }

        // Keep local cache strictly in sync with authoritative database value:
        if (isArchived) {
          cachedArchivedSet.add(classIdStr);
        } else {
          cachedArchivedSet.delete(classIdStr);
        }

        return normalizeClassroom(classroom, index, {
          students: memberCountByClass[classroom.id] ?? 0,
          assignments: assignmentCountByClass[classroom.id] ?? 0,
          submissions: submissionCountByClass[classroom.id] ?? 0,
          isArchived,
        });
      });

    try {
      const storageKeys = [
        `writecheck_archived_classes_${teacherId}`,
        "writecheck_archived_classes_teacher",
        "writecheck_archived_classes_global",
      ];
      storageKeys.forEach((key) => {
        localStorage.setItem(key, JSON.stringify(Array.from(cachedArchivedSet)));
      });
    } catch {}

    const classroomsById =
      new Map(nextClassrooms.map((classroom) => [classroom.id, classroom]));

    const submissionCountByAssignment =
      submissionRows.reduce((counts, submission) => {
        counts[submission.assignment_id] =
          (counts[submission.assignment_id] ?? 0) + 1;
        return counts;
      }, {});

    const nextAssignments =
      assignmentRows.map((assignment) =>
        normalizeAssignment(assignment, classroomsById, {
          submissions: submissionCountByAssignment[assignment.id] ?? 0,
        })
      );

    const studentsById =
      new Map(
        studentRows.map((student) => [
          student.id,
          student.full_name || student.email || "Student",
        ])
      );

    rosterNames.forEach((name, studentId) => {
      studentsById.set(studentId, name);
    });

    const assignmentsById =
      new Map(nextAssignments.map((assignment) => [assignment.id, assignment]));

    const nextSubmissions =
      submissionRows.map((submission) => {
        const assignment =
          assignmentsById.get(submission.assignment_id);

        return {
          id: submission.id,
          createdAt: submission.created_at,
          assignmentId: submission.assignment_id,
          classroomId: submission.classroom_id,
          studentId: submission.student_id,
          revisionRequest: submission.revision_request,
          revisionHistory: submission.revision_history || [],
          returnedAt: submission.returned_at,
          studentName: studentsById.get(submission.student_id) || "Student",
          assignmentTitle: assignment?.title || "Assignment",
          classroomName: assignment?.classroomName || "Classroom",
          essayTitle: submission.essay_title || "Essay submission",
          fileUrl: submission.file_url,
          status: submission.status || "submitted",
          grade: submission.grade ?? "",
          feedback: submission.feedback ?? "",
          transcribedText: submission.transcribed_text ?? "",
          scanResult: submission.scan_result ?? null,
          processingState: submissionStates[submission.id]?.state || "submitted",
          processingError: submissionStates[submission.id]?.error || null,
        };
      });

    const usersById = new Map(studentRows.map((user) => [user.id, user]));
    const nextClassroomMembers = memberRows.map((member) => {
      const user = usersById.get(member.student_id);
      return {
        id: member.id,
        classroomId: member.classroom_id,
        studentId: member.student_id,
        studentName: studentsById.get(member.student_id) || "Student",
        studentEmail: user?.email || "",
      };
    });

    if (requestId !== teacherDataRequestRef.current) {
      return false;
    }

    setClassroomMembers(nextClassroomMembers);
    setClassrooms(nextClassrooms);
    setAssignments(nextAssignments);
    setSubmissions(nextSubmissions);
    setSelectedAssignmentId((currentId) =>
      currentId && nextAssignments.some((a) => a.id === currentId)
        ? currentId
        : null
    );
    setAssignmentForm((currentForm) => ({
      ...currentForm,
      classroomId:
        nextClassrooms.some((classroom) => classroom.id === currentForm.classroomId)
          ? currentForm.classroomId
          : "",
    }));
    teacherDataLoadedOnce.current = true;
    setIsLoading(false);
    return { assignments: nextAssignments, submissions: nextSubmissions };
  }, [profile?.id]);

  useEffect(() => {
    if (profile?.id) {
      loadTeacherData();
    }
  }, [profile?.id, loadTeacherData]);

  useEffect(() => {
    if (!supabase.auth?.onAuthStateChange) return undefined;
    const {
      data: { subscription } = {},
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Do not refetch on TOKEN_REFRESHED or redundant SIGNED_IN events when switching browser tabs
      if (event === "USER_UPDATED" || (event === "SIGNED_IN" && session?.user?.id && session.user.id !== profile?.id)) {
        loadTeacherData(true);
      }
    });

    return () => subscription?.unsubscribe?.();
  }, [loadTeacherData, profile?.id]);


  useEffect(() => {
    const imageFile =
      manualCheckFiles.find((file) => file.type?.startsWith("image/"));

    if (!imageFile) {
      setManualImagePreview("");
      return undefined;
    }

    const previewUrl =
      URL.createObjectURL(imageFile);

    setManualImagePreview(previewUrl);

    return () => URL.revokeObjectURL(previewUrl);
  }, [manualCheckFiles]);

  useEffect(() => {
    let isMounted = true;
    getOcrEngineInfo().then((info) => {
      if (isMounted && info) {
        setOcrEngineInfo(info);
      }
    });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!isLoading && archivedClassrooms.length === 0 && classroomTab === "archived") setClassroomTab("active");
  }, [isLoading, archivedClassrooms.length, classroomTab, setClassroomTab]);

  const handleArchiveClassroom = async (classroomId, archive = true) => {
    if (!classroomId) return;
    setIsArchiving(true);
    setErrorMessage("");
    const classIdStr = String(classroomId);

    // Save snapshot of previous state for reliable rollback on failure
    const previousClassrooms = classrooms;
    const previousTab = classroomTab;

    try {
      // 1. Optimistically update local state so UI is immediately responsive
      setClassrooms((prev) =>
        prev.map((c) =>
          String(c.id) === classIdStr ? { ...c, isArchived: archive } : c
        )
      );

      // When restoring, switch immediately to "active" tab so the user sees the restored class
      if (!archive) {
        setClassroomTab("active");
      }

      // 2. Persist to backend service role endpoint (guaranteed to persist is_archived in Supabase Postgres)
      let backendUpdated = false;
      let failureReason = "";

      try {
        const backendUrl = getBackendUrl();
        const response = await apiFetch(`${backendUrl}/api/classrooms/${classroomId}/archive`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_archived: archive }),
        });
        const resBody = await response.json().catch(() => ({}));
        if (response.ok && resBody?.success) {
          backendUpdated = true;
        } else {
          failureReason =
            resBody?.detail ||
            resBody?.message ||
            resBody?.error ||
            `Server responded with HTTP ${response.status}`;
          console.warn("Backend archive response not ok:", response.status, resBody);
        }
      } catch (backendErr) {
        failureReason = backendErr.message || "Unable to reach backend server";
        console.warn("Backend archive call failed, trying direct Supabase:", backendErr);
      }

      // 3. Fallback: Try direct Supabase RPC archive_classroom (SECURITY DEFINER with teacher ownership check)
      if (!backendUpdated) {
        try {
          const { data: rpcSuccess, error: rpcErr } = await supabase.rpc("archive_classroom", {
            target_classroom_id: classroomId,
            should_archive: archive,
          });
          if (!rpcErr && rpcSuccess === true) {
            backendUpdated = true;
          } else if (rpcErr) {
            console.warn("archive_classroom RPC notice:", rpcErr);
          }
        } catch (rpcEx) {
          console.warn("archive_classroom RPC call notice:", rpcEx);
        }
      }

      // 4. Fallback: Try direct Supabase table update with row count verification
      if (!backendUpdated) {
        try {
          const { data: updatedRows, error: sbErr } = await supabase
            .from(CLASSROOM_TABLE)
            .update({ is_archived: archive })
            .eq("id", classroomId)
            .select("id, is_archived");
          if (!sbErr && Array.isArray(updatedRows) && updatedRows.length > 0) {
            backendUpdated = true;
          } else if (sbErr) {
            console.warn("Direct Supabase update notice:", sbErr);
          }
        } catch (sbErr) {
          console.warn("Direct Supabase update notice:", sbErr);
        }
      }

      // 5. If all persistence attempts failed, ROLLBACK optimistic state and alert the user
      if (!backendUpdated) {
        setClassrooms(previousClassrooms);
        setClassroomTab(previousTab);
        throw new Error(
          archive
            ? `Could not archive classroom on server: ${failureReason || "Database update failed. Please check your backend connection."}`
            : `Could not restore classroom on server: ${failureReason || "Database update failed. Please check your backend connection."}`
        );
      }

      // 6. Confirmed server persistence: update localStorage cache across all keys
      const storageKeys = [
        `writecheck_archived_classes_${profile?.id || "teacher"}`,
        "writecheck_archived_classes_teacher",
        "writecheck_archived_classes_global",
      ];
      storageKeys.forEach((key) => {
        try {
          const stored = JSON.parse(localStorage.getItem(key) || "[]");
          const set = new Set(Array.isArray(stored) ? stored.map(String) : []);
          if (archive) {
            set.add(classIdStr);
          } else {
            set.delete(classIdStr);
          }
          localStorage.setItem(key, JSON.stringify(Array.from(set)));
        } catch (cacheErr) {
          console.warn("Could not cache archived classroom ID:", cacheErr);
        }
      });

      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("writecheck:classroom_archived", {
            detail: { classroomId: classIdStr, isArchived: archive },
          })
        );
      }

      const target = previousClassrooms.find((c) => String(c.id) === classIdStr);
      const targetName = target?.name || "Classroom";
      setSuccessMessage(
        archive
          ? `Classroom "${targetName}" archived. You can find it in the Archived classes tab.`
          : `Classroom "${targetName}" restored to active classes.`
      );

      // 7. Silently refresh teacher data from DB to guarantee tab counts and lists are completely synced
      try {
        await loadTeacherData(true);
      } catch (syncErr) {
        console.warn("Silent teacher data sync notice:", syncErr);
      }
    } catch (err) {
      setErrorMessage(err.message || "Failed to update classroom archive status.");
    } finally {
      setIsArchiving(false);
      setArchivingClassroom(null);
    }
  };

  const handleOpenCopyModal = (classroom) => {
    setCopyingClassroom(classroom);
    setCopyForm({
      name: `Copy of ${classroom.name}`,
      section: classroom.section || "",
      subject:
        classroom.subject && classroom.subject !== "No subject"
          ? classroom.subject
          : classroom.name,
    });
  };

  const handleConfirmCopyClassroom = async (e) => {
    e.preventDefault();
    if (!copyingClassroom || !copyForm.name.trim() || !copyForm.section.trim()) {
      setErrorMessage("Enter a class name and section.");
      return;
    }
    setIsCopying(true);
    setErrorMessage("");

    try {
      const className = copyForm.name.trim();
      const section = copyForm.section.trim();
      const subject = copyForm.subject.trim() || className;

      // 1. Generate unique class code and create classroom
      let newClassroom = null;
      for (let attempt = 0; attempt < 5; attempt += 1) {
        const classroomCode = buildClassCode(className);
        const { data, error } = await supabase
          .from(CLASSROOM_TABLE)
          .insert({
            teacher_id: profile.id,
            teacher_name: profile.full_name || profile.email || "Teacher",
            classroom_name: className,
            classroom_code: classroomCode,
            subject: subject || null,
            section,
          })
          .select()
          .single();

        if (error?.code === "23505") continue;
        if (error) throw error;
        newClassroom = data;
        break;
      }

      if (!newClassroom) {
        throw new Error("Could not generate a unique class code. Please try again.");
      }

      // 2. Clone assignments from source classroom
      const { data: sourceAssignments, error: aError } = await supabase
        .from(ASSIGNMENT_TABLE)
        .select("title, instructions, due_date, accept_late_submissions")
        .eq("classroom_id", copyingClassroom.id);

      let copiedCount = 0;
      if (!aError && Array.isArray(sourceAssignments) && sourceAssignments.length > 0) {
        const clonedAssignments = sourceAssignments.map((a) => ({
          classroom_id: newClassroom.id,
          teacher_id: profile.id,
          title: a.title,
          instructions: a.instructions,
          due_date: a.due_date,
          accept_late_submissions: a.accept_late_submissions ?? true,
        }));
        const { error: insErr } = await supabase.from(ASSIGNMENT_TABLE).insert(clonedAssignments);
        if (!insErr) {
          copiedCount = clonedAssignments.length;
        }
      }

      setCopyingClassroom(null);
      setClassroomTab("active");
      setSuccessMessage(
        `Class copied! "${className}" created with code ${newClassroom.classroom_code} (${copiedCount} assignment${copiedCount === 1 ? "" : "s"} duplicated).`
      );
      await loadTeacherData();
    } catch (err) {
      setErrorMessage(err.message || "Failed to copy classroom.");
    } finally {
      setIsCopying(false);
    }
  };

  const handleExportClassroomCSV = async (classroom) => {
    try {
      const classId = classroom.id;
      const classSubmissions = submissions.filter((s) => s.classroomId === classId);

      const { data: members } = await supabase
        .from(MEMBER_TABLE)
        .select("student_id, student_name, student_email, created_at")
        .eq("classroom_id", classId);

      const studentList = members || [];
      const rows = [
        ["Classroom", classroom.name],
        ["Section", classroom.section],
        ["Subject", classroom.subject || ""],
        ["Status", classroom.isArchived ? "Archived" : "Active"],
        ["Export Date", new Date().toLocaleDateString()],
        [],
        ["Student Name", "Student Email", "Joined Date", "Assignments Submitted", "Average Grade", "Flagged Submissions"],
      ];

      if (studentList.length === 0) {
        rows.push(["No enrolled students in this classroom"]);
      } else {
        studentList.forEach((m) => {
          const studentSubs = classSubmissions.filter((s) => s.studentId === m.student_id);
          const gradedSubs = studentSubs.filter(
            (s) => s.grade !== undefined && s.grade !== null && s.grade !== ""
          );
          const avgGrade =
            gradedSubs.length > 0
              ? (
                  gradedSubs.reduce((acc, s) => acc + (parseFloat(s.grade) || 0), 0) /
                  gradedSubs.length
                ).toFixed(1)
              : "N/A";
          const flaggedCount = studentSubs.filter(
            (s) => s.status === "flagged" || s.scanResult?.similarityScore > 20
          ).length;
          rows.push([
            m.student_name || "Student",
            m.student_email || "",
            m.created_at ? new Date(m.created_at).toLocaleDateString() : "",
            studentSubs.length,
            avgGrade,
            flaggedCount,
          ]);
        });
      }

      const csvContent =
        "data:text/csv;charset=utf-8," +
        rows.map((e) => e.map((val) => `"${String(val).replace(/"/g, '""')}"`).join(",")).join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute(
        "download",
        `${classroom.name.replace(/[^a-zA-Z0-9_-]/g, "_")}_summary.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSuccessMessage(`Exported CSV for "${classroom.name}".`);
    } catch (err) {
      setErrorMessage("Could not export classroom CSV: " + err.message);
    }
  };

  const handleCreateClassroom = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsSavingClassroom(true);

    const className =
      classroomForm.name.trim();

    const section =
      classroomForm.section.trim();

    const subject =
      className;

    if (!className || !section) {
      setErrorMessage("Enter a subject name and section.");
      setIsSavingClassroom(false);
      return;
    }

    for (let attempt = 0; attempt < 5; attempt += 1) {
      const classroomCode =
        buildClassCode(className);

      const { error } =
        await supabase
          .from(CLASSROOM_TABLE)
          .insert({
            teacher_id: profile.id,
            teacher_name: profile.full_name || profile.email || "Teacher",
            classroom_name: className,
            classroom_code: classroomCode,
            subject: subject || null,
            section,
          });

      if (error?.code === "23505") {
        continue;
      }

      if (error) {
        setErrorMessage(error.message);
        setIsSavingClassroom(false);
        return;
      }

      setClassroomForm(emptyClassroomForm);
      setIsCreatingClassroom(false);
      setClassroomTab("active");
      setSuccessMessage(`Class created. Code: ${classroomCode}`);
      setIsSavingClassroom(false);
      await loadTeacherData();
      return;
    }

    setErrorMessage("Could not generate a unique class code. Try again.");
    setIsSavingClassroom(false);
  };

  const handleCreateAssignment = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsSavingAssignment(true);

    const classroomId =
      assignmentForm.classroomId;
    if (!classroomId || !activeClassrooms.some((c) => c.id === classroomId)) {
      setErrorMessage("Open an active classroom before creating an assignment.");
      setIsSavingAssignment(false);
      return;
    }

    const dueDate =
      assignmentForm.dueDate
        ? new Date(assignmentForm.dueDate).toISOString()
        : null;

    const payload = {
      classroom_id: classroomId,
      teacher_id: profile.id,
      title: assignmentForm.title.trim(),
      instructions: assignmentForm.instructions.trim() || null,
      due_date: dueDate,
      accept_late_submissions: assignmentForm.acceptLateSubmissions !== false,
    };

    let { error } = await supabase.from(ASSIGNMENT_TABLE).insert(payload);

    if (error && (error.message?.includes("accept_late_submissions") || error.code === "42703" || error.code === "PGRST204")) {
      delete payload.accept_late_submissions;
      const retry = await supabase.from(ASSIGNMENT_TABLE).insert(payload);
      error = retry.error;
    }

    if (error) {
      setErrorMessage(error.message);
      setIsSavingAssignment(false);
      return;
    }

    setAssignmentForm({
      ...emptyAssignmentForm,
      classroomId,
    });
    setIsCreatingAssignment(false);
    setSuccessMessage("Assignment created.");
    setIsSavingAssignment(false);
    await loadTeacherData();
  };

  const handleAssignmentDeleted = async ({ id, classroomId }) => {
    let refreshed = false;
    try { refreshed = await loadTeacherData(true); } catch { /* Deletion already committed. */ }
    // The server has committed. Keep stale refreshes from resurrecting this item.
    setAssignments(rows => rows.filter(row => String(row.id) !== String(id)));
    setSubmissions(rows => rows.filter(row => String(row.assignmentId) !== String(id)));
    setSelectedAssignmentId(null);
    setCommentTarget(null);
    setLinkedScanAssignmentId(current => String(current) === String(id) ? "all" : current);
    if (String(loadedSubmissionInfo?.assignmentId) === String(id)) setLoadedSubmissionInfo(null);
    if (String(reviewingSubmission?.assignmentId) === String(id)) {
      setReviewingSubmission(null);
      setGradeInput("");
      setFeedbackInput("");
    }
    setEditingAssignment(null);
    setDeletingAssignment(null);
    setOpenedClassroomId(classroomId);
    setAssignmentFilterClassroomId(classroomId);
    setActivePage("assignments");
    setErrorMessage(refreshed ? "" : "The assignment was deleted, but the workspace could not refresh. Reload to update the remaining records.");
    setSuccessMessage("Assignment deleted successfully.");
  };

  const handleOpenEditAssignment = (assignment) => {
    setEditingAssignment({
      id: assignment.id,
      classroomId: assignment.classroomId,
      title: assignment.title || "",
      instructions: assignment.instructions || "",
      dueDate: toDateTimeLocalInput(assignment.dueDate),
      acceptLateSubmissions: assignment.acceptLateSubmissions !== false,
    });
  };

  const handleCloseEditAssignment = () => {
    setEditingAssignment(null);
    setIsUpdatingAssignment(false);
  };

  const handleUpdateAssignment = async (event) => {
    event.preventDefault();
    if (!editingAssignment) return;

    setErrorMessage("");
    setSuccessMessage("");
    setIsUpdatingAssignment(true);

    try {
      const dueDate = editingAssignment.dueDate
        ? new Date(editingAssignment.dueDate).toISOString()
        : null;

      const updatePayload = {
        classroom_id: editingAssignment.classroomId,
        title: editingAssignment.title.trim(),
        instructions: editingAssignment.instructions?.trim() || null,
        due_date: dueDate,
        accept_late_submissions: editingAssignment.acceptLateSubmissions !== false,
      };

      let { error: updateError } = await supabase
        .from(ASSIGNMENT_TABLE)
        .update(updatePayload)
        .eq("id", editingAssignment.id)
        .eq("classroom_id", editingAssignment.classroomId)
        .eq("teacher_id", profile.id);

      if (updateError && (updateError.message?.includes("accept_late_submissions") || updateError.code === "42703" || updateError.code === "PGRST204")) {
        delete updatePayload.accept_late_submissions;
        const retry = await supabase
          .from(ASSIGNMENT_TABLE)
          .update(updatePayload)
          .eq("id", editingAssignment.id)
          .eq("classroom_id", editingAssignment.classroomId)
          .eq("teacher_id", profile.id);
        updateError = retry.error;
      }

      if (updateError) {
        if (process.env.NODE_ENV === "development") {
          console.error("Assignment update failed:", {
            assignmentId: editingAssignment.id,
            error: updateError,
          });
        }
        setErrorMessage(updateError.message || "Failed to update assignment.");
        return;
      }

      // Verify the persisted values separately. Update responses may omit rows
      // when the project's Supabase policies do not allow returning rows.
      const { data: updatedAssignment, error: verifyError } = await supabase
        .from(ASSIGNMENT_TABLE)
        .select("id, created_at, classroom_id, teacher_id, title, instructions, due_date")
        .eq("id", editingAssignment.id)
        .eq("classroom_id", editingAssignment.classroomId)
        .eq("teacher_id", profile.id)
        .maybeSingle();

      if (verifyError) {
        if (process.env.NODE_ENV === "development") {
          console.error("Assignment update verification failed:", {
            assignmentId: editingAssignment.id,
            error: verifyError,
          });
        }
        setErrorMessage(verifyError.message || "Could not verify the assignment update.");
        return;
      }

      const savedValuesMatch =
        updatedAssignment &&
        updatedAssignment.classroom_id === editingAssignment.classroomId &&
        updatedAssignment.title === editingAssignment.title.trim() &&
        (updatedAssignment.instructions || null) ===
          (editingAssignment.instructions?.trim() || null) &&
        (updatedAssignment.due_date
          ? new Date(updatedAssignment.due_date).getTime()
          : null) === (dueDate ? new Date(dueDate).getTime() : null);

      if (!savedValuesMatch) {
        if (process.env.NODE_ENV === "development") {
          console.error("Assignment update values did not persist:", {
            assignmentId: editingAssignment.id,
            expected: {
              classroomId: editingAssignment.classroomId,
              title: editingAssignment.title.trim(),
              instructions: editingAssignment.instructions?.trim() || null,
              dueDate,
            },
            actual: updatedAssignment,
          });
        }
        setErrorMessage(
          "The assignment was not updated. It may have been removed or you no longer have permission to edit it."
        );
        return;
      }

      const classroomsById = new Map(
        classrooms.map((classroom) => [classroom.id, classroom])
      );
      const currentAssignment = assignments.find(
        (assignment) => assignment.id === updatedAssignment.id
      );

      setAssignments((currentAssignments) =>
        currentAssignments.map((assignment) =>
          assignment.id === updatedAssignment.id
            ? normalizeAssignment(updatedAssignment, classroomsById, {
                submissions: currentAssignment?.submissions ?? assignment.submissions,
              })
            : assignment
        )
      );

      const didRefresh = await loadTeacherData();
      if (!didRefresh) {
        if (process.env.NODE_ENV === "development") {
          console.error("Assignment saved but dashboard refresh failed:", {
            assignmentId: updatedAssignment.id,
          });
        }
        setErrorMessage("The assignment was saved, but the dashboard could not refresh its data.");
        return;
      }

      setEditingAssignment(null);
      setSuccessMessage("Assignment updated successfully.");
    } catch (err) {
      if (process.env.NODE_ENV === "development") {
        console.error("Assignment update request failed:", err);
      }
      setErrorMessage(err.message || "Could not update assignment.");
    } finally {
      setIsUpdatingAssignment(false);
    }
  };

  const handleCopyTranscript = (textToCopy) => {
    const text = textToCopy || transcribedText || manualLiveOcrText;
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    }
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleTransferToText = () => {
    if (isTranscribing || isScanningManualCheck || !transcriptionResult) return;
    const text = transcribedText;
    if (!text) return;
    setManualCheckText(text);
    setUploadMode("text");
  };

  const handleLoadSampleEssay = async () => {
    try {
      setManualCheckError("");
      const response = await apiFetch("/samples/sample_student_essay.jpg");
      if (!response.ok) throw new Error("Sample file not found");
      const blob = await response.blob();
      const sampleFile = new File([blob], "sample_student_essay.jpg", {
        type: "image/jpeg",
      });

      setManualCheckFiles([sampleFile]);
      setUploadMode("picture");
      setManualCheckResult(null);
      setTranscribedText("");
      setTranscriptionResult(null);
      setManualLiveOcrResult(null);
      setShowLineBreakdown(false);
    } catch {
      setManualCheckError("Could not load sample handwritten essay image.");
    }
  };

  const handleTranscribePicture = async () => {
    if (!manualCheckFiles.length || !manualCheckFiles.every(isEssayImage)) {
      setManualCheckError("Choose supported images for the pages of one essay.");
      return null;
    }
    setIsTranscribing(true);
    setManualCheckError("");
    setTranscribedText("");
    setTranscriptionResult(null);
    setManualLiveOcrResult(null);
    try {
      const result = await extractTextFromImages(manualCheckFiles, {
        onProgress: progress => setManualLiveOcrResult(progress),
      });
      setTranscribedText(result.text);
      setTranscriptionResult(result);
      setManualLiveOcrResult(result);
      return result;
    } catch (error) {
      setManualCheckError(error.message || "Failed to transcribe handwriting.");
      return null;
    } finally {
      setIsTranscribing(false);
    }
  };

  const changeManualPages = async nextFiles => {
    const selection = ++manualSelection.current;
    try {
      if (uploadMode === "picture") validateEssayPages(nextFiles, uploadLimits);
      else await Promise.all(nextFiles.map(validateDocumentContents));
    } catch (error) { if (selection === manualSelection.current) setManualCheckError(error.message); return; }
    if (selection !== manualSelection.current) return;
    // Changing the preview order invalidates the previously assembled essay.
    setManualCheckFiles(nextFiles);
    setManualCheckResult(null);
    setManualLiveOcrResult(null);
    setTranscribedText("");
    setTranscriptionResult(null);
    setShowLineBreakdown(false);
    setManualCheckError("");
  };

  const handleManualCheckFiles = (event) => {
    const nextFiles =
      Array.from(event.target.files ?? []);

    changeManualPages(nextFiles);
    event.target.value = "";

    if (nextFiles.length > 0 && uploadMode === "text") {
      setUploadMode("file");
    }
  };

  const handleRunManualCheck = async (event) => {
    event.preventDefault();
    setManualCheckError("");
    setManualCheckResult(null);

    const hasImage = uploadMode !== "text" && manualCheckFiles.some(isEssayImage);

    if (!manualCheckText.trim() && manualCheckFiles.length === 0 && !transcribedText.trim()) {
      setManualCheckError("Add a picture, file, or pasted text before scanning.");
      return;
    }

    setIsScanningManualCheck(true);

    try {
      if (uploadMode === "file") await Promise.all(manualCheckFiles.map(validateDocumentContents));
      let fileText;

      if (hasImage) {
        if (!manualCheckFiles.every(isEssayImage)) throw new Error("Upload images only for a multi-page handwritten essay.");
        const result = transcriptionResult?.pages?.length === manualCheckFiles.length
          ? { ...transcriptionResult, text: manualCheckFiles.length > 1 ? combinePageText(transcriptionResult.pages) : transcribedText.trim() }
          : await handleTranscribePicture();
        if (!result) return; // Never scan partial text after a failed page.
        if (!result.text.trim()) throw new Error("Review every page before checking similarity.");
        fileText = { text: result.text, extractedText: result.text, extractedImages: result.pages,
          readableFiles: manualCheckFiles, unreadableFiles: [] };
      } else if (uploadMode === "text") {
        fileText = { text: "", extractedText: manualCheckText, extractedImages: [], readableFiles: [], unreadableFiles: [] };
      } else {
        fileText = await readTextFromFiles(manualCheckFiles);
      }

      // Each recognized word is included once. File labels and page headings
      // are presentation only and never become similarity input.
      const combinedText = hasImage ? fileText.text : [manualCheckText, fileText.text].map(value => value.trim()).filter(Boolean).join("\n\n");

      let scanResult = null;
      const nonImageFile = uploadMode === "text" ? null : manualCheckFiles.find((f) => !isEssayImage(f));
      if (nonImageFile || combinedText.trim().length >= 15) {
        setPlagiarismScanProgressText("Submitting to Copyleaks Plagiarism API...");

        try {
          const submission = await checkPlagiarismViaBackend({
            text: combinedText,
            file: nonImageFile || null,
            filename: manualCheckTitle.trim() || nonImageFile?.name || "essay.txt",
            userId: profile?.id || "teacher",
          });

          setPlagiarismScanProgressText("Analyzing sources & text with Copyleaks...");
          scanResult = await pollPlagiarismScanResult(submission.scan_id, {
            onProgress: (pScan, attempt) => {
              setPlagiarismScanProgressText(`Analyzing sources with Copyleaks (check ${attempt})...`);
            },
          });
        } catch (scanErr) {
          throw scanErr;
        }
      }

      if (scanResult?.status !== "completed" || scanResult?.result_data?.provider !== "copyleaks") {
        throw new Error("No verified Copyleaks result is available. Please run a new check.");
      }
      const finalScore = scanResult.plagiarism_score;

      const finalTone =
        finalScore >= 50 ? "red" : finalScore >= 20 ? "amber" : "emerald";

      const finalLabel =
        finalScore >= 50
          ? "High review"
          : finalScore >= 20
            ? "Medium review"
            : "Low review";

      setManualCheckResult({
        flags: [],
        repeatedPhrases: [],
        title: manualCheckTitle.trim() || "Plagiarism check",
        score: finalScore,
        tone: finalTone,
        label: finalLabel,
        wordCount: scanResult.total_words ?? 0,
        identicalWords: scanResult?.identical_words ?? 0,
        scanStatus: "Completed",
        provider: "copyleaks",
        scanId: scanResult.scan_id,
        matchedSources: scanResult?.result_data?.matched_sources || [],
        extractedText: fileText.extractedText,
        extractedImages: fileText.extractedImages,
        readableFiles: fileText.readableFiles,
        unreadableFiles: fileText.unreadableFiles,
        imageUrl:
          manualImagePreview ||
          (manualCheckFiles[0]?.name ? `${resolveBackendUrl()}/uploads/${manualCheckFiles[0].name}` : ""),
        imageName: manualCheckFiles[0]?.name || manualCheckTitle || "Submission image",
        summary: "Verified Copyleaks API result.",
      });
    } catch (error) {
      setManualCheckError(error.message || "Could not scan the selected material.");
    } finally {
      setIsScanningManualCheck(false);
      setPlagiarismScanProgressText("");
    }
  };

  const handleResetManualCheck = () => {
    if (isTranscribing || isScanningManualCheck) return;
    setManualCheckTitle("");
    setManualCheckText("");
    setManualCheckFiles([]);
    setManualImagePreview("");
    setManualCheckResult(null);
    setManualLiveOcrResult(null);
    setTranscribedText("");
    setTranscriptionResult(null);
    setShowLineBreakdown(false);
    setManualCheckError("");
    setUploadMode("");
    setPlagiarismScanProgressText("");
    setIsEditingTranscript(false);
    setIsManualImageExpanded(false);
    setLoadedSubmissionInfo(null);
    setLoadingSubmissionId(null);
    setLinkedScanAssignmentId("all");
  };

  const loadedSubmission = loadedSubmissionInfo
    ? submissions.find((row) => String(row.id) === String(loadedSubmissionInfo.id)) || loadedSubmissionInfo
    : null;

  const handleLoadSubmissionToScanStation = (sub) => {
    if (!sub) return;
    setManualCheckResult(null);
    setManualLiveOcrResult(null);
    setManualCheckFiles([]);
    setManualCheckText("");
    setTranscribedText("");
    setManualCheckError("");
    setLoadedSubmissionInfo(sub);
    requestAnimationFrame(() => {
      document.getElementById("scan-station-form-top")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleOpenReview = (submission) => {
    setReviewingSubmission(submission);
    setGradeInput(String(submission.grade ?? ""));
    setFeedbackInput(String(submission.feedback ?? ""));
    setReviewCopySuccess(false);
    setReviewImagePreviewUrl("");
    setIsImageExpanded(false);

    // Calculate actual peer submissions for THIS assignment and classroom/section only
    const currentAssignmentId = submission.assignmentId || submission.assignment_id;
    const currentClassroomId = submission.classroomId || submission.classroom_id;
    const classmateCount = (submissions || []).filter(
      (s) =>
        (s.assignmentId || s.assignment_id) === currentAssignmentId &&
        (!currentClassroomId || (s.classroomId || s.classroom_id) === currentClassroomId) &&
        s.id !== submission.id
    ).length;

    if (submission.scanResult && (!submission.processingState || submission.processingState === "ready")) {
      let initialScanResult = { ...submission.scanResult };
      if (classmateCount === 0 && initialScanResult.peerComparisonEnabled !== false) {
        initialScanResult.peerSimilarity = {
          peer_similarity_score: 0.0,
          has_peer_match: false,
          highest_match_submission_id: null,
          matched_submission_label: null,
          matching_snippets: [],
          all_matches: [],
          total_peers_compared: 0,
        };
        initialScanResult.peerScore = 0.0;
      } else if (
        initialScanResult.peerSimilarity &&
        initialScanResult.peerSimilarity.total_peers_compared > classmateCount
      ) {
        initialScanResult.peerSimilarity = {
          ...initialScanResult.peerSimilarity,
          total_peers_compared: classmateCount,
        };
      }
      setReviewScanResult(initialScanResult);
      setReviewTranscribedText(submission.transcribedText || initialScanResult.transcribedText || "");
    } else {
      setReviewScanResult(null);
      setReviewTranscribedText(submission.transcribedText || "");
    }

    // Load image preview
    const fileUrl = submission.fileUrl;
    if (!fileUrl) return;
    const isImageUrl = /\.(jpe?g|png|webp|gif)$/i.test(fileUrl) ||
      /\.(jpe?g|png|webp|gif)/i.test(fileUrl);

    if (isImageUrl) {
      setIsLoadingImagePreview(true);
      resolveStorageImageUrl(fileUrl)
        .then((resolvedUrl) => {
          if (resolvedUrl) setReviewImagePreviewUrl(resolvedUrl);
        })
        .catch((err) => {
          console.warn("Failed to resolve image preview URL:", err);
        })
        .finally(() => setIsLoadingImagePreview(false));
    }
  };

  const handleCloseReview = () => {
    if (isSavingGrade || isReviewScanning) return;
    setReviewingSubmission(null);
    setGradeInput("");
    setFeedbackInput("");
    setReviewScanResult(null);
    setReviewTranscribedText("");
    setIsReviewScanning(false);
    setReviewScanProgressText("");
    setReviewCopySuccess(false);
    setReviewImagePreviewUrl("");
    setIsLoadingImagePreview(false);
    setIsImageExpanded(false);
  };

  const handleCopyReviewTranscript = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setReviewCopySuccess(true);
      setTimeout(() => setReviewCopySuccess(false), 2000);
    } catch {
      // ignore clipboard error
    }
  };

  const handleRunReviewScan = async () => {
    if (!reviewingSubmission || isReviewScanning || isSavingGrade) return;
    setIsReviewScanning(true);
    setErrorMessage("");
    try {
      const { data, error } = await supabase.rpc("retry_submission_processing", {
        submission_key: String(reviewingSubmission.id),
        corrected_text: reviewingSubmission.transcribedText || null,
      });
      if (error || !data?.state) throw new Error(error?.message || "Could not queue this check.");
      const update = (row) => ({ ...row, returnedAt: null, processingState: data.state, processingError: null });
      setSubmissions((rows) => rows.map((row) => row.id === reviewingSubmission.id ? update(row) : row));
      setReviewingSubmission(update);
      setSuccessMessage("Check queued. You can close this window; processing continues on the server.");
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsReviewScanning(false);
      setReviewScanProgressText("");
    }
  };

  useEffect(() => {
    if (!reviewingSubmission) return;
    const latest = submissions.find((row) => row.id === reviewingSubmission.id);
    if (!latest || isSavingGrade || (latest.processingState === reviewingSubmission.processingState
      && latest.processingError === reviewingSubmission.processingError
      && latest.fileUrl === reviewingSubmission.fileUrl
      && latest.createdAt === reviewingSubmission.createdAt
      && latest.grade === reviewingSubmission.grade
      && latest.feedback === reviewingSubmission.feedback
      && latest.returnedAt === reviewingSubmission.returnedAt
      && latest.status === reviewingSubmission.status
      && JSON.stringify(latest.revisionRequest) === JSON.stringify(reviewingSubmission.revisionRequest)
      && JSON.stringify(latest.revisionHistory) === JSON.stringify(reviewingSubmission.revisionHistory)
      && latest.classroomName === reviewingSubmission.classroomName
      && latest.transcribedText === reviewingSubmission.transcribedText
      && JSON.stringify(latest.scanResult) === JSON.stringify(reviewingSubmission.scanResult))) return;
    // Revisions replace the canonical row in place. Never carry an old attempt's
    // grade draft into its replacement; preserve drafts on ordinary polling.
    if (latest.fileUrl !== reviewingSubmission.fileUrl || latest.createdAt !== reviewingSubmission.createdAt
      || (reviewingSubmission.revisionRequest && !latest.revisionRequest)) {
      setGradeInput(latest.grade ?? "");
      setFeedbackInput(latest.feedback ?? "");
    }
    setReviewingSubmission((current) => ({ ...current, ...latest }));
    if (latest.processingState === "ready") {
      setReviewTranscribedText(latest.transcribedText || "");
      setReviewScanResult(latest.scanResult || null);
    } else {
      setReviewScanResult(null);
    }
  }, [submissions, reviewingSubmission, isReviewScanning, isSavingGrade]);

  const gradeSaveInFlight = useRef(false);
  const handleSaveGrade = async (event, returnWork = false) => {
    if (event) event.preventDefault();
    if (!reviewingSubmission || gradeSaveInFlight.current || isSavingGrade || isReviewScanning) return;

    gradeSaveInFlight.current = true;
    setIsSavingGrade(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const gradeVal = String(gradeInput ?? "").trim();
      const feedbackVal = String(feedbackInput ?? "").trim();
      const subId = reviewingSubmission.id;
      const backendUrl = resolveBackendUrl();
      const response = await apiFetch(`${backendUrl}/api/submissions/${subId}/grade`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          grade: gradeVal, feedback: feedbackVal,
          status: gradeVal ? "graded" : "submitted",
          assignment_id: reviewingSubmission.assignmentId || reviewingSubmission.assignment_id,
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success || !result.grade_record) {
        throw new Error(typeof result.detail === "string" ? result.detail : "Grade was not saved. Please try again.");
      }
      const saved = result.grade_record;
      setSubmissions((prev) => prev.map((submission) => submission.id === subId
        ? { ...submission, grade: saved.grade ?? "", feedback: saved.feedback ?? "",
            returnedAt: saved.returned_at, status: saved.status, transcribedText: saved.transcribed_text ?? "",
            scanResult: saved.scan_result ?? null }
        : submission));
      setSuccessMessage(`Grade saved for ${reviewingSubmission.studentName}.`);
      setReviewingSubmission((current) => ({ ...current, grade: saved.grade ?? "",
        feedback: saved.feedback ?? "", returnedAt: saved.returned_at, status: saved.status,
        transcribedText: saved.transcribed_text ?? "", scanResult: saved.scan_result ?? null }));
      if (returnWork) {
        const { data: released, error: releaseError } = await supabase.rpc("return_submission", {
          submission_key: String(subId),
        });
        if (releaseError || !released?.returned_at) {
          throw new Error(`Grade saved, but could not return work. ${releaseError?.message || "Please retry Return work."}`);
        }
        setSubmissions((rows) => rows.map((row) => row.id === subId
          ? { ...row, returnedAt: released.returned_at } : row));
        setReviewingSubmission((row) => ({ ...row, returnedAt: released.returned_at }));
        setSuccessMessage("Work returned. The student can now see the saved results.");
      }
    } catch (error) {
      setSuccessMessage("");
      setErrorMessage(error.message || "Could not save. Your changes are still here; please retry.");
    } finally {
      gradeSaveInFlight.current = false;
      setIsSavingGrade(false);
    }
  };

  const handleRequestRevision = async () => {
    if (isSavingGrade || !reviewingSubmission) return;
    const message = feedbackInput.trim();
    if (!message) { setErrorMessage("Enter revision instructions in Teacher Feedback first."); return; }
    setIsSavingGrade(true);
    setErrorMessage("");
    try {
      const { error } = await supabase.rpc("request_submission_revision", { submission_key: String(reviewingSubmission.id), instructions: message });
      if (error) throw error;
      setReviewingSubmission(null);
      await loadTeacherData();
      setSuccessMessage("Revision requested. The student can submit updated work; the earlier submission is preserved.");
    } catch (error) { setErrorMessage(error.message || "Could not request a revision."); }
    finally { setIsSavingGrade(false); }
  };

  const handleReturnWork = () => {
    if (!String(gradeInput ?? "").trim()) {
      setErrorMessage("Enter a grade before returning work.");
      return;
    }
    return handleSaveGrade(null, true);
  };

  const manualResultBadgeClass =
    manualCheckResult?.tone === "red"
      ? "bg-red-50 text-red-700"
      : manualCheckResult?.tone === "amber"
        ? "bg-amber-50 text-amber-700"
        : "bg-emerald-50 text-emerald-700";

  const manualResultRingClass =
    manualCheckResult?.tone === "red"
      ? "text-red-700 ring-red-100"
      : manualCheckResult?.tone === "amber"
        ? "text-amber-700 ring-amber-100"
        : "text-emerald-700 ring-emerald-100";

  const manualDetectedText =
    (typeof manualCheckResult?.extractedText === "string"
      ? manualCheckResult.extractedText
      : Array.isArray(manualCheckResult?.extractedText)
        ? manualCheckResult.extractedText.join("\n\n")
        : (manualCheckResult?.transcribedText || transcribedText || manualCheckText || "")
    )?.trim() ?? "";

  const manualLiveOcrText =
    (typeof manualLiveOcrResult?.text === "string"
      ? manualLiveOcrResult.text.trim()
      : "") ?? "";

  const hasManualImageExtraction =
    (manualCheckResult?.extractedImages?.length ?? 0) > 0 ||
    uploadMode === "picture" ||
    Boolean(transcriptionResult?.detectedLineCount || manualLiveOcrResult?.detectedLineCount);

  const manualImageExtractionSummary =
    (manualCheckResult?.extractedImages ?? [])
      .flatMap((image) => {
        const summaries = [];

        if (image.duplicateLineCount > 0) {
          summaries.push(
            `${image.name}: removed ${image.duplicateLineCount} duplicate detected line${image.duplicateLineCount === 1 ? "" : "s"}.`
          );
        }

        if (image.truncated) {
          summaries.push(
            `${image.name}: showing ${image.processedLineCount} of ${image.detectedLineCount} detected lines for faster OCR.`
          );
        }

        return summaries;
      })
      .filter(Boolean);

  const renderAssignmentDetailsAndSubmissions = (onBack, embedded = false) => {
    if (!selectedAssignment) return null;

    return (
      <div className="space-y-6">
        {/* Navigation Breadcrumb & Actions */}
        {!embedded && <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#dadce0] pb-4">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onBack || clearAssignmentContext}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#dadce0] bg-white px-3.5 py-1.5 text-xs font-medium text-[#3c4043] transition hover:bg-[#f8f9fa] hover:border-[#137333]"
            >
              <ArrowLeftIcon className="h-3.5 w-3.5" />
              <span>Back to Classwork</span>
            </button>
            <span className="text-gray-300 font-bold">/</span>
            <span className="text-sm font-medium text-[#202124]">
              {selectedAssignment.classroomName}
            </span>
            <span className="inline-flex items-center rounded-md bg-[#f1f3f4] px-2 py-0.5 text-xs font-medium text-[#3c4043]">
              Section {selectedAssignment.classroomSection || "Standard"}
            </span>
          </div>

          <button type="button" disabled={exportingGrades}
            onClick={() => handleExportGrades({ assignmentId: selectedAssignment.id })}
            className="rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {exportingGrades ? "Exporting..." : "Export Grades"}
          </button>

          <button
            type="button"
            onClick={() => handleOpenEditAssignment(selectedAssignment)}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#dadce0] bg-white px-4 py-1.5 text-xs font-medium text-[#3c4043] transition hover:bg-[#f8f9fa] hover:border-[#137333]"
          >
            <EditIcon className="h-3.5 w-3.5 text-[#5f6368]" />
            <span>Edit assignment</span>
          </button>
          {profile?.role === "teacher" && <details className="relative text-sm">
            <summary className="similarity-chip cursor-pointer">More actions</summary>
            <div className="mt-2 border-t border-gray-200 pt-2">
              <button type="button" className="validation-error rounded-lg px-3 py-2" onClick={() => {
                setSuccessMessage("");
                setErrorMessage("");
                setDeletingAssignment(selectedAssignment);
              }}>Delete assignment</button>
            </div>
          </details>}
        </div>}

        {/* Assignment Header Info */}
        <div className="rounded-xl border border-[#dadce0] bg-white p-6 shadow-2xs">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex items-center rounded-md bg-[#e6f4ea] px-2.5 py-0.5 text-xs font-medium text-[#137333]">
                  {selectedAssignment.classroomName}
                </span>
                <span className="inline-flex items-center rounded-md bg-[#f1f3f4] px-2.5 py-0.5 text-xs font-medium text-[#3c4043]">
                  Section {selectedAssignment.classroomSection || "All"}
                </span>
                {selectedAssignment.classroomSubject && selectedAssignment.classroomSubject !== "No subject" && selectedAssignment.classroomSubject !== selectedAssignment.classroomName && (
                  <span className="inline-flex items-center rounded-md bg-[#f1f3f4] px-2.5 py-0.5 text-xs font-medium text-[#5f6368]">
                    {selectedAssignment.classroomSubject}
                  </span>
                )}
              </div>

              <h1 className="mt-3 text-2xl font-medium text-[#202124] tracking-tight break-words">
                {selectedAssignment.title}
              </h1>

              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#5f6368]">
                <span>
                  Due: <strong className="text-[#202124] font-medium">{formatDateTime(selectedAssignment.dueDate)}</strong>
                </span>
                <span>•</span>
                <span>100 points</span>
                <span>•</span>
                <span>
                  Posted: {formatDateTime(selectedAssignment.createdAt)}
                </span>
              </div>
            </div>
          </div>

          {/* Google Classroom Sub-tabs: Student work vs Instructions */}
          <div className="mt-6 flex border-b border-[#dadce0] text-sm">
            <button
              type="button"
              onClick={() => setAssignmentDetailTab("roster")}
              className={`px-4 py-2.5 font-medium transition-colors ${
                assignmentDetailTab === "roster"
                  ? "border-b-2 border-[#137333] text-[#137333]"
                  : "border-b-2 border-transparent text-[#5f6368] hover:text-[#202124]"
              }`}
            >
              Student work ({assignmentStats.total})
            </button>
            <button
              type="button"
              onClick={() => setAssignmentDetailTab("instructions")}
              className={`px-4 py-2.5 font-medium transition-colors ${
                assignmentDetailTab === "instructions"
                  ? "border-b-2 border-[#137333] text-[#137333]"
                  : "border-b-2 border-transparent text-[#5f6368] hover:text-[#202124]"
              }`}
            >
              Instructions
            </button>
          </div>

          {assignmentDetailTab === "instructions" ? (
            <div className="mt-6 space-y-4">
              <div className="rounded-lg border border-[#dadce0] bg-[#f8f9fa] p-5">
                <h4 className="text-xs font-medium uppercase tracking-wider text-[#5f6368]">
                  Assignment Instructions
                </h4>
                <p className="mt-3 text-sm text-[#202124] leading-relaxed whitespace-pre-wrap">
                  {selectedAssignment.instructions || "No detailed instructions provided for this assignment."}
                </p>
              </div>

              <div className="text-xs text-[#5f6368] space-y-1">
                <p>Class: <strong className="text-[#202124]">{selectedAssignment.classroomName}</strong></p>
                <p>Section: <strong className="text-[#202124]">{selectedAssignment.classroomSection || "Standard"}</strong></p>
                <p>Class code: <strong className="font-mono text-[#202124]">{selectedAssignment.classroomCode}</strong></p>
              </div>
            </div>
          ) : (
            <>
              {/* Google Classroom Student Work Metric Counters */}
              <div className="mt-6 grid grid-cols-3 gap-4 border-b border-[#dadce0] pb-6">
                <div className="rounded-xl border border-[#dadce0] bg-[#f8f9fa] p-4 text-center">
                  <span className="text-xs font-medium text-[#137333] uppercase">
                    Turned in
                  </span>
                  <p className="mt-1 text-3xl font-medium text-[#137333]">
                    {assignmentStats.submitted}
                  </p>
                </div>

                <div className="rounded-xl border border-[#dadce0] bg-[#f8f9fa] p-4 text-center">
                  <span className="text-xs font-medium text-[#5f6368] uppercase">
                    Assigned / Missing
                  </span>
                  <p className="mt-1 text-3xl font-medium text-[#5f6368]">
                    {assignmentStats.missing}
                  </p>
                </div>

                <div className="rounded-xl border border-[#dadce0] bg-[#f8f9fa] p-4 text-center">
                  <span className="text-xs font-medium text-[#1967d2] uppercase">
                    Graded
                  </span>
                  <p className="mt-1 text-3xl font-medium text-[#1967d2]">
                    {assignmentStats.graded}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {assignmentDetailTab === "roster" && (
        <div className="space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-xl font-black text-gray-950">
                Student Submissions ({assignmentRoster.length})
              </h3>
              <p className="mt-0.5 text-xs font-bold text-gray-500">
                Students enrolled in <span className="text-emerald-700 font-extrabold">{selectedAssignment.classroomName} — Section: {selectedAssignment.classroomSection}</span>
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <ValidatedInput
                  type="text"
                  value={submissionRosterSearch}
                  onChange={(e) => setSubmissionRosterSearch(e.target.value)}
                  placeholder="Filter student..."
                  className="h-9 w-40 sm:w-52 rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-xs font-semibold text-gray-900 outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                />
              </div>

              <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 text-xs font-extrabold">
                <button
                  type="button"
                  onClick={() => setSubmissionRosterFilter("all")}
                  className={`rounded-md px-2.5 py-1 transition ${
                    submissionRosterFilter === "all"
                      ? "bg-emerald-700 text-white shadow-2xs"
                      : "text-gray-600 hover:text-gray-950"
                  }`}
                >
                  All ({assignmentStats.total})
                </button>
                <button
                  type="button"
                  onClick={() => setSubmissionRosterFilter("submitted")}
                  className={`rounded-md px-2.5 py-1 transition ${
                    submissionRosterFilter === "submitted"
                      ? "bg-emerald-700 text-white shadow-2xs"
                      : "text-gray-600 hover:text-gray-950"
                  }`}
                >
                  Submitted ({assignmentStats.submitted})
                </button>
                <button
                  type="button"
                  onClick={() => setSubmissionRosterFilter("graded")}
                  className={`rounded-md px-2.5 py-1 transition ${
                    submissionRosterFilter === "graded"
                      ? "bg-emerald-700 text-white shadow-2xs"
                      : "text-gray-600 hover:text-gray-950"
                  }`}
                >
                  Graded ({assignmentStats.graded})
                </button>
                <button
                  type="button"
                  onClick={() => setSubmissionRosterFilter("missing")}
                  className={`rounded-md px-2.5 py-1 transition ${
                    submissionRosterFilter === "missing"
                      ? "bg-emerald-700 text-white shadow-2xs"
                      : "text-gray-600 hover:text-gray-950"
                  }`}
                >
                  Missing ({assignmentStats.missing})
                </button>
              </div>
            </div>
          </div>

          {/* Submissions Roster Table */}
          {assignmentStats.total > 0 && assignmentStats.submitted === 0 && (
            <div className="rounded-xl border border-dashed border-[#dadce0] bg-white px-5 py-6 text-center">
              <p className="text-sm font-medium text-[#202124]">No submissions yet</p>
              <p className="mt-1 text-xs text-[#5f6368]">
                Students who submit this assignment will appear here.
              </p>
            </div>
          )}
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xs">
            <div className="wc-data-heading grid grid-cols-[1.3fr_1fr_1.2fr_1.1fr_0.9fr] gap-4 border-b border-gray-200 bg-gray-50/75 px-5 py-3.5 text-xs font-extrabold uppercase tracking-wider text-gray-500">
              <span>Student</span>
              <span>Status</span>
              <span>Submitted File</span>
              <span>Grade & Feedback</span>
              <span className="text-right">Action</span>
            </div>

            {filteredAssignmentRoster.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm font-bold text-gray-500">
                  {assignmentRoster.length === 0
                    ? "No students enrolled in this classroom section yet."
                    : "No students match the current filter."}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                <PaginatedList items={filteredAssignmentRoster} resetKey={[selectedAssignmentId, submissionRosterFilter, submissionRosterSearch]} label="Assignment submissions">{pageItems => pageItems.map((item) => (
                  <div
                    key={item.studentId}
                    className="wc-data-row grid grid-cols-[1.3fr_1fr_1.2fr_1.1fr_0.9fr] items-center gap-4 px-5 py-4 text-sm transition hover:bg-gray-50/50"
                  >
                    <div data-label="Student" className="flex items-center gap-3 min-w-0">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-800">
                        {item.studentName.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="truncate font-black text-gray-950">
                          {item.studentName}
                        </p>
                        {item.studentEmail && (
                          <p className="truncate text-xs font-semibold text-gray-500">
                            {item.studentEmail}
                          </p>
                        )}
                      </div>
                    </div>

                    <div data-label="Status">
                      {item.status === "revision" && <span className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800">Revision requested</span>}
                      {item.status === "graded" && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-black text-emerald-800">
                          <CheckIcon className="h-3.5 w-3.5" />
                          Graded
                        </span>
                      )}
                      {item.status === "late" && (
                        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-black text-amber-800">
                          Late Submission
                        </span>
                      )}
                      {item.status === "submitted" && (
                        <span className="inline-flex items-center rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-black text-blue-800">
                          Submitted
                        </span>
                      )}
                      {item.status === "missing" && (
                        <span className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2.5 py-1 text-xs font-black text-rose-800">
                          Missing
                        </span>
                      )}
                    </div>

                    <div data-label="Submitted file" className="min-w-0">
                      {item.submission && <SimilarityPreview submission={item.submission} submissions={submissions} onReview={handleOpenReview} />}
                      {item.fileUrl ? (
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => openSubmissionFile(item.fileUrl, setErrorMessage)}
                            className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900 hover:underline truncate"
                            title="Open submitted file"
                          >
                            <FileIcon className="h-4 w-4 shrink-0 text-emerald-600" />
                            <span className="truncate">{item.essayTitle || "View Submission"}</span>
                            <ExternalLinkIcon className="h-3 w-3 shrink-0 text-gray-400" />
                          </button>
                          <button
                            type="button"
                            onClick={() => downloadSubmissionFileBlob(item.fileUrl, item.studentName)}
                            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                            title="Download file"
                          >
                            <DownloadIcon className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs font-bold text-gray-600">
                          No file submitted
                        </span>
                      )}
                    </div>

                    <div data-label="Grade & feedback" className="min-w-0">
                      {item.grade ? (
                        <div>
                          <span className="inline-flex items-center rounded-md border border-emerald-300 bg-emerald-100 px-2.5 py-0.5 text-xs font-black text-emerald-900">
                            {item.grade}
                          </span>
                          {item.feedback && (
                            <p className="mt-1 truncate text-xs font-semibold text-gray-600" title={item.feedback}>
                              "{item.feedback}"
                            </p>
                          )}
                        </div>
                      ) : item.isSubmitted ? (
                        <span className="text-xs font-bold text-gray-400 italic">
                          Not graded yet
                        </span>
                      ) : (
                        <span className="text-xs font-bold text-gray-300">—</span>
                      )}
                    </div>

                    <div data-label="Action" className="text-right">
                      {item.submission ? (
                        <><button
                          type="button"
                          onClick={() => handleOpenReview(item.submission)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-extrabold text-white transition hover:bg-emerald-800 shadow-2xs"
                        >
                          <FileSearchIcon className="h-3.5 w-3.5" />
                          <span>{item.grade ? "Review" : "Review & Grade"}</span>
                        </button>
                        <button type="button" onClick={() => setPeerInspection({ submission: item.submission, matchedId: null })}
                          className="mt-2 rounded-lg border border-[#dadce0] px-3 py-1.5 text-xs font-medium text-[#137333]">Peer similarity</button></>
                      ) : (
                        <span className="text-xs font-bold text-gray-300">
                          No submission
                        </span>
                      )}
                    </div>
                  </div>
                ))}</PaginatedList>
              </div>
            )}
          </div>
        </div>
        )}
        {!reviewingSubmission && <AssignmentComments key={`${selectedAssignment.classroomId}:${selectedAssignment.id}`} assignmentId={selectedAssignment.id} classroomId={selectedAssignment.classroomId} profile={profile} target={commentTarget} />}
      </div>
    );
  };

  const openNotification = async (notification) => {
    const fresh = await loadTeacherData(true);
    if (!fresh) throw new Error("Could not refresh this submission. Please retry.");
    const assignment = fresh.assignments.find(a => String(a.id) === notification.assignment_id && String(a.classroomId) === notification.classroom_id);
    const submission = fresh.submissions.find(s => String(s.id) === notification.submission_id && String(s.assignmentId) === notification.assignment_id && String(s.classroomId) === notification.classroom_id);
    const isComment = ["student_class_comment", "student_private_comment"].includes(notification.type);
    if (!assignment || (!isComment && !submission)) throw new Error("This assignment or submission is no longer available to you.");
    return () => {
      setOpenedClassroomId(assignment.classroomId);
      setAssignmentFilterClassroomId(assignment.classroomId);
      setSelectedSubmissionsClassroomId(assignment.classroomId);
      setSelectedAssignmentId(assignment.id);
      setActivePage("assignments");
      if (isComment) {
        setReviewingSubmission(null);
        setCommentTarget({ assignmentId: assignment.id, kind: notification.type === "student_private_comment" ? "private" : "class", studentId: notification.metadata?.student_id, id: notification.id });
      } else { setCommentTarget(null); handleOpenReview(submission); }
    };
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] text-[#202124]">
      <Header
        profile={profile}
        onProfileUpdated={onProfileUpdated}
        workspace="Teacher workspace"
        unreadNotifications={notifications.unread}
        notifications={<NotificationBell model={notifications} onOpen={openNotification} onViewAll={() => { clearAssignmentContext(); setActivePage("notifications"); }} />}
        pages={teacherPages}
        activePage={activePage}
        onPageChange={(page) => {
          setSuccessMessage("");
          setErrorMessage("");
          if (page === "submissions" && activePage !== "submissions") setGradesOrigin(openedClassroomId || null);
          if (page === "submissions" && openedClassroomId &&
              String(selectedAssignment?.classroomId) !== String(openedClassroomId)) {
            selectGradeClassroom(openedClassroomId);
          } else if (page === "submissions" && selectedAssignment) {
            selectGradeAssignment(selectedAssignment.id);
          } else if ((page === "assignments" || page === "submissions" || page === "calendar") && openedClassroomId) {
            setAssignmentFilterClassroomId(openedClassroomId);
            setSelectedSubmissionsClassroomId(openedClassroomId);
            clearAssignmentContext();
          }
          if (!["assignments", "submissions", "upload"].includes(page)) clearAssignmentContext();
          if (page === "upload" && activePage !== "upload") setLinkedScanAssignmentId(selectedAssignment?.id || "all");
          setActivePage(page);
        }}
      />

      <main className="mx-auto max-w-[1280px] px-3.5 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {activePage === "notifications" && <NotificationsPage model={notifications} onOpen={openNotification} />}
        <div className="mb-4 sm:mb-6">
          <StatusMessage
            error={errorMessage || submissionSyncError}
            message={successMessage}
          />
        </div>

        {activePage === "calendar" && (isLoading ? <p role="status">Loading assignments...</p> :
          <AssignmentCalendar assignments={assignments.map(a => ({ ...a, isArchived: classrooms.find(c => c.id === a.classroomId)?.isArchived }))}
            classrooms={classrooms} classroomId={assignmentFilterClassroomId === "all" ? "" : assignmentFilterClassroomId}
            onClassroomChange={id => { clearAssignmentContext(); setAssignmentFilterClassroomId(id || "all"); setOpenedClassroomId(id || null); }}
            onOpenAssignment={assignment => { setAssignmentFilterClassroomId(assignment.classroomId); setOpenedClassroomId(assignment.classroomId); setSelectedAssignmentId(assignment.id); setActivePage("assignments"); }} />)}
        <section className="space-y-6">

        {/* Create Classroom Modal Dialog */}
        {isCreatingClassroom && (
          <div className="wc-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
            <div className="wc-modal-panel w-full max-w-lg rounded-2xl border border-[#dadce0] bg-white p-6 shadow-xl">
              <div className="wc-modal-heading flex items-start justify-between pb-4 border-b border-[#dadce0]">
                <div>
                  <h3 className="text-xl font-medium text-[#202124]">Create class</h3>
                  <p className="mt-0.5 text-xs text-[#5f6368]">Enter details for your new classroom section.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreatingClassroom(false);
                    setClassroomForm(emptyClassroomForm);
                  }}
                  className="rounded-full p-1 text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#202124]"
                >
                  ✕
                </button>
              </div>

              <ValidatedForm onSubmit={handleCreateClassroom} className="mt-5 space-y-4">
                <label className="block">
                  <span className="text-xs font-medium text-[#3c4043]">Classroom name (required)</span>
                  <ValidatedInput
                    type="text"
                    value={classroomForm.name}
                    onChange={(e) => setClassroomForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. English 10, Biology Honors"
                    className="mt-1.5 h-11 w-full rounded-md border border-[#dadce0] px-3 text-sm text-[#202124] outline-none transition focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium text-[#3c4043]">Section (required)</span>
                  <ValidatedInput
                    type="text"
                    value={classroomForm.section}
                    onChange={(e) => setClassroomForm((f) => ({ ...f, section: e.target.value }))}
                    placeholder="e.g. Period 1, Section A"
                    className="mt-1.5 h-11 w-full rounded-md border border-[#dadce0] px-3 text-sm text-[#202124] outline-none transition focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                    required
                  />
                </label>

                <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-[#dadce0]">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreatingClassroom(false);
                      setClassroomForm(emptyClassroomForm);
                    }}
                    className="rounded-full px-4 py-2 text-sm font-medium text-[#5f6368] hover:bg-[#f1f3f4]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingClassroom}
                    className="rounded-full bg-[#137333] px-5 py-2 text-sm font-medium text-white hover:bg-[#0f5b28] disabled:bg-gray-300"
                  >
                    {isSavingClassroom ? "Creating..." : "Create"}
                  </button>
                </div>
              </ValidatedForm>
            </div>
          </div>
        )}

        {/* Create Assignment Modal Dialog */}
        {isCreatingAssignment && (
          <div className="wc-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
            <div className="wc-modal-panel w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-2xl border border-[#dadce0] bg-white p-6 shadow-xl">
              <div className="wc-modal-heading flex items-start justify-between pb-4 border-b border-[#dadce0]">
                <div>
                  <h3 className="text-xl font-medium text-[#202124]">Create assignment</h3>
                  <p className="mt-0.5 text-xs text-[#5f6368]">Post instructions and due date for your classroom section.</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreatingAssignment(false);
                    setAssignmentForm(emptyAssignmentForm);
                  }}
                  className="rounded-full p-1 text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#202124]"
                >
                  ✕
                </button>
              </div>

              <ValidatedForm onSubmit={handleCreateAssignment} className="mt-5 space-y-4">
                <p className="text-sm text-[#5f6368]">{classrooms.find((c) => c.id === assignmentForm.classroomId)?.name}</p>

                <label className="block">
                  <span className="text-xs font-medium text-[#3c4043]">Title</span>
                  <ValidatedInput
                    type="text"
                    value={assignmentForm.title}
                    onChange={(e) => setAssignmentForm((f) => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Narrative Essay Draft"
                    className="mt-1.5 h-11 w-full rounded-md border border-[#dadce0] px-3 text-sm text-[#202124] outline-none transition focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                    required
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium text-[#3c4043]">Instructions (optional)</span>
                  <ValidatedTextarea
                    value={assignmentForm.instructions}
                    onChange={(e) => setAssignmentForm((f) => ({ ...f, instructions: e.target.value }))}
                    placeholder="Add instructions, prompt details, and guidelines for students..."
                    rows="4"
                    className="mt-1.5 w-full rounded-md border border-[#dadce0] p-3 text-sm text-[#202124] outline-none transition focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                  />
                </label>

                <label className="block">
                  <span className="text-xs font-medium text-[#3c4043]">Due date</span>
                  <ValidatedInput
                    type="datetime-local"
                    value={assignmentForm.dueDate}
                    onChange={(e) => setAssignmentForm((f) => ({ ...f, dueDate: e.target.value }))}
                    className="mt-1.5 h-11 w-full rounded-md border border-[#dadce0] px-3 text-sm text-[#202124] outline-none transition focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                  />
                </label>

                <div className="rounded-xl border border-[#dadce0] bg-[#f8f9fa] p-3.5">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <ValidatedInput
                      type="checkbox"
                      checked={assignmentForm.acceptLateSubmissions !== false}
                      onChange={(e) => setAssignmentForm((f) => ({ ...f, acceptLateSubmissions: e.target.checked }))}
                      className="mt-0.5 h-4 w-4 rounded border-[#dadce0] text-[#137333] focus:ring-[#137333]"
                    />
                    <div>
                      <span className="text-sm font-medium text-[#202124]">Allow late submissions</span>
                      <p className="mt-0.5 text-xs text-[#5f6368]">
                        If checked, students can turn in work after the deadline (marked as Late). If unchecked, late submissions are blocked once overdue.
                      </p>
                    </div>
                  </label>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-[#dadce0]">
                  <button
                    type="button"
                    onClick={() => {
                      setIsCreatingAssignment(false);
                      setAssignmentForm(emptyAssignmentForm);
                    }}
                    className="rounded-full px-4 py-2 text-sm font-medium text-[#5f6368] hover:bg-[#f1f3f4]"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSavingAssignment}
                    className="rounded-full bg-[#137333] px-5 py-2 text-sm font-medium text-white hover:bg-[#0f5b28] disabled:bg-gray-300"
                  >
                    {isSavingAssignment ? "Assigning..." : "Assign"}
                  </button>
                </div>
              </ValidatedForm>
            </div>
          </div>
        )}

        {activePage === "classrooms" && openedClassroomId && classrooms.some((c) => c.id === openedClassroomId) && (
          <ClassroomDetail
            key={openedClassroomId}
            classroom={classrooms.find((c) => c.id === openedClassroomId)}
            assignments={assignments}
            teacher={{ id: profile?.id, name: profile?.full_name || profile?.email || "Teacher", email: profile?.email || "" }}
            onBack={() => { clearAssignmentContext(); setOpenedClassroomId(null); }}
            onOpenAssignment={(assignment) => {
              setAssignmentFilterClassroomId(openedClassroomId);
              setSelectedAssignmentId(assignment.id);
              setAssignmentDetailTab("roster");
              setActivePage("assignments");
            }}
            onCreateAssignment={() => {
              setAssignmentForm({ ...emptyAssignmentForm, classroomId: openedClassroomId });
              setIsCreatingAssignment(true);
            }}
            onToggleArchive={(classroomId, archive) => {
              const cl = classrooms.find((c) => String(c.id) === String(classroomId));
              setArchivingClassroom({
                id: classroomId,
                name: cl?.name || "Classroom",
                archive,
              });
            }}
            onCopyClassroom={handleOpenCopyModal}
            onExportCSV={handleExportClassroomCSV}
            onExportGrades={(classroomId) => handleExportGrades({ classroomId })}
            onRenamed={row => {
              setClassrooms(current => current.map(c => String(c.id) === String(row.id) ? { ...c, name: row.classroom_name } : c));
              setSubmissions(current => current.map(item => String(item.classroomId) === String(row.id) ? { ...item, classroomName: row.classroom_name } : item));
            }}
            onMemberRemoved={() => { loadTeacherData(); }}
          />
        )}

        {/* Classes Page */}
        {activePage === "classrooms" && (!openedClassroomId || !classrooms.some((c) => c.id === openedClassroomId)) && (
          <div className="space-y-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-[#dadce0] pb-5">
              <div>
                <h2 className="text-2xl font-medium tracking-tight text-[#202124]">
                  Classes
                </h2>
                <p className="mt-1 text-sm text-[#5f6368]">
                  Manage your classes, share student join codes, and oversee assignments.
                </p>
              </div>

              <button
                type="button"
                onClick={() => {
                  setClassroomForm(emptyClassroomForm);
                  setIsCreatingClassroom(true);
                }}
                className="inline-flex items-center gap-2 rounded-full bg-[#137333] px-5 py-2.5 text-sm font-medium text-white shadow-xs transition hover:bg-[#0f5b28] active:scale-[0.98]"
              >
                <PlusIcon className="h-4 w-4" />
                <span>Create class</span>
              </button>
            </div>

            {/* Active vs Archived Filter Tabs */}
            <div className="flex items-center gap-2 border-b border-[#dadce0]">
              <button
                type="button"
                onClick={() => setClassroomTab("active")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition -mb-px ${
                  classroomTab === "active"
                    ? "border-[#137333] text-[#137333] font-semibold"
                    : "border-transparent text-[#5f6368] hover:text-[#202124]"
                }`}
              >
                <span>Active classes</span>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                    classroomTab === "active"
                      ? "bg-[#e6f4ea] text-[#137333]"
                      : "bg-[#f1f3f4] text-[#5f6368]"
                  }`}
                >
                  {activeClassrooms.length}
                </span>
              </button>

              {archivedClassrooms.length > 0 && (
              <button
                type="button"
                onClick={() => setClassroomTab("archived")}
                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition -mb-px ${
                  classroomTab === "archived"
                    ? "border-amber-600 text-amber-800 font-semibold"
                    : "border-transparent text-[#5f6368] hover:text-[#202124]"
                }`}
              >
                <ArchiveIcon className="h-4 w-4" />
                <span>Archived classes</span>
                {archivedClassrooms.length > 0 && (
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">
                    {archivedClassrooms.length}
                  </span>
                )}
              </button>
              )}
            </div>

            {isLoading ? (
              <div className="wc-classroom-grid grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="h-64 rounded-xl border border-[#dadce0] bg-white animate-pulse" />
                ))}
              </div>
            ) : classroomTab === "active" && activeClassrooms.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#dadce0] bg-white p-12 text-center max-w-md mx-auto my-8">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#e6f4ea] text-[#137333]">
                  <DoorIcon className="h-7 w-7" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-[#202124]">No active classes</h3>
                <p className="mt-1 text-sm text-[#5f6368]">
                  {archivedClassrooms.length > 0
                    ? "All your classes are currently archived. Create a new class or restore one from the Archived classes tab."
                    : "Create a class to start posting assignments and tracking student submissions."}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setClassroomForm(emptyClassroomForm);
                    setIsCreatingClassroom(true);
                  }}
                  className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#137333] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#0f5b28]"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Create class</span>
                </button>
              </div>
            ) : classroomTab === "archived" && archivedClassrooms.length === 0 ? (
              <div className="rounded-xl border border-dashed border-[#dadce0] bg-white p-12 text-center max-w-md mx-auto my-8">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-50 text-amber-700">
                  <ArchiveIcon className="h-7 w-7" />
                </div>
                <h3 className="mt-4 text-lg font-medium text-[#202124]">No archived classes</h3>
                <p className="mt-1 text-sm text-[#5f6368]">
                  Classes you archive will be moved here in read-only mode. You can restore them to active at any time.
                </p>
              </div>
            ) : (
              <div className="wc-classroom-grid grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {visibleClassrooms.map((classroom) => {
                  const creatorName = profile?.full_name || classroom.teacherName || "Teacher";
                  const creatorInitials = getInitials(creatorName, "T");
                  const creatorTheme = getTeacherAvatarTheme(classroom.teacherId || profile?.id || classroom.teacherName);
                  const rawAvatar = (classroom.teacherId === profile?.id || !classroom.teacherId) ? (profile?.avatarUrl || "") : (classroom.teacherAvatarUrl || (classroom.teacherId ? getAvatarPublicUrl(classroom.teacherId) : ""));
                  const creatorAvatarUrl = isCustomAvatarUrl(rawAvatar) ? rawAvatar : "";

                  return (
                  <article
                    key={classroom.id}
                    className={`group flex flex-col rounded-xl border bg-white overflow-hidden shadow-2xs hover:shadow-md transition-shadow duration-200 ${
                      classroom.isArchived ? "border-amber-300 ring-1 ring-amber-200" : "border-[#dadce0]"
                    }`}
                  >
                    <div className={`relative h-32 p-4 text-white flex flex-col justify-between ${classroom.accent}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1 pr-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3>
                              <button type="button" onClick={() => selectGradeClassroom(classroom.id)}
                                className="text-left text-xl font-medium tracking-tight text-white hover:underline break-words"
                                title={classroom.name}>
                                {classroom.name}
                              </button>
                            </h3>
                            {classroom.isArchived && (
                              <span className="inline-flex items-center gap-1 rounded bg-amber-900/70 border border-amber-300/40 px-2 py-0.5 text-[11px] font-semibold text-amber-100 shadow-xs">
                                <ArchiveIcon className="h-3 w-3" />
                                Archived
                              </span>
                            )}
                          </div>
                          <p className="text-xs font-normal text-white/90 truncate mt-0.5">
                            Section {classroom.section} {classroom.subject && classroom.subject !== classroom.name ? `• ${classroom.subject}` : ""}
                          </p>
                        </div>
                        <ClassroomInformation classroom={classroom} teacherView />
                      </div>

                      <div
                        className="group/avatar absolute -bottom-6 right-4 z-10"
                        title={`Instructor & Classroom Creator: ${creatorName}`}
                      >
                        <div
                          className={`flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-tr ${creatorTheme.bg} text-white text-lg font-bold shadow-md ring-4 ring-white transition-all duration-200 group-hover/avatar:scale-105 select-none overflow-hidden`}
                        >
                          {creatorAvatarUrl ? (
                            <img
                              src={creatorAvatarUrl}
                              alt={creatorName}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const span = e.currentTarget.parentElement?.querySelector(".avatar-initials-fallback");
                                if (span) span.style.display = "flex";
                              }}
                              className="h-full w-full object-cover"
                            />
                          ) : null}
                          <span
                            className="avatar-initials-fallback flex items-center justify-center"
                            style={{ display: creatorAvatarUrl ? "none" : "flex" }}
                          >
                            {creatorInitials}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 pt-7 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between gap-2">
                          <ClassCode key={classroom.id} code={classroom.code} />

                          <span className="text-xs text-[#5f6368]">
                            {classroom.students} {classroom.students === 1 ? "student" : "students"}
                          </span>
                        </div>

                        <p className="mt-3 text-xs text-[#5f6368]">
                          {classroom.assignments} assignments posted • {classroom.submissions} turned in
                        </p>

                        {classroom.isArchived && (
                          <div className="mt-2.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-1.5 text-xs text-amber-800">
                            Class is archived (read-only)
                          </div>
                        )}
                      </div>

                      <div>
                        {/* Quick Navigation Links */}
                        <div className="mt-4 pt-3 border-t border-[#e0e0e0] flex items-center justify-between text-xs font-medium">
                          <button
                            type="button"
                            onClick={() => {
                              setOpenedClassroomId(classroom.id);
                              setAssignmentFilterClassroomId(classroom.id);
                              clearAssignmentContext();
                              setActivePage("assignments");
                            }}
                            className="inline-flex items-center gap-1 text-[#137333] hover:underline"
                          >
                            <ClipboardIcon className="h-3.5 w-3.5" />
                            <span>Classwork</span>
                          </button>

                          <button
                            type="button"
                            onClick={() => {
                              selectGradeClassroom(classroom.id);
                              setGradesOrigin(classroom.id);
                              setActivePage("submissions");
                            }}
                            className="inline-flex items-center gap-1 text-[#5f6368] hover:text-[#202124]"
                          >
                            <FileSearchIcon className="h-3.5 w-3.5" />
                            <span>Gradebook</span>
                          </button>
                        </div>

                        {/* Actions Row */}
                        <div className="mt-2.5 pt-2 border-t border-gray-100 flex items-center justify-between text-xs gap-2">
                          <button
                            type="button"
                            onClick={() => selectGradeClassroom(classroom.id)}
                            className="font-medium text-[#137333] hover:underline shrink-0"
                          >
                            Open classroom
                          </button>

                          <div className="flex items-center gap-2">
                            {/* Copy Class Button (Google Classroom feature) */}
                            <button
                              type="button"
                              onClick={() => handleOpenCopyModal(classroom)}
                              className="inline-flex items-center gap-1 text-xs font-medium text-[#5f6368] hover:text-[#137333] hover:underline"
                              title="Copy class for new term (duplicates assignments)"
                            >
                              <CopyIcon className="h-3.5 w-3.5" />
                              <span>Copy</span>
                            </button>

                            {/* Export CSV for archived classes */}
                            {classroom.isArchived && (
                              <button
                                type="button"
                                onClick={() => handleExportClassroomCSV(classroom)}
                                className="inline-flex items-center gap-1 text-xs font-medium text-[#5f6368] hover:text-[#137333] hover:underline"
                                title="Export summary and grades to CSV"
                              >
                                <DownloadIcon className="h-3.5 w-3.5" />
                                <span>Export</span>
                              </button>
                            )}

                            {classroom.isArchived ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setArchivingClassroom({
                                    id: classroom.id,
                                    name: classroom.name,
                                    archive: false,
                                  })
                                }
                                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 hover:text-amber-950 hover:underline"
                                title="Restore classroom to active"
                              >
                                <UnarchiveIcon className="h-3.5 w-3.5" />
                                <span>Restore</span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() =>
                                  setArchivingClassroom({
                                    id: classroom.id,
                                    name: classroom.name,
                                    archive: true,
                                  })
                                }
                                className="inline-flex items-center gap-1 text-xs font-medium text-[#5f6368] hover:text-amber-800 hover:underline"
                                title="Archive classroom"
                              >
                                <ArchiveIcon className="h-3.5 w-3.5" />
                                <span>Archive</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </article>
                );
              })}
              </div>
            )}
          </div>
        )}

        {/* Classwork Page */}
        {activePage === "assignments" && (
          selectedAssignment ? (
            renderAssignmentDetailsAndSubmissions(clearAssignmentContext)
          ) : (
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#dadce0] pb-4 sm:pb-5">
                  <div>
                    <h2 className="text-xl sm:text-2xl font-medium tracking-tight text-[#202124]">
                      Classwork
                    </h2>
                    <p className="mt-0.5 sm:mt-1 text-xs sm:text-sm text-[#5f6368]">
                      Create and organize assignments, review student submissions, and grade work.
                    </p>
                  </div>

                  <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => {
                        const classroom = activeClassrooms.find((c) => c.id === assignmentFilterClassroomId);
                        if (!classroom) {
                          clearAssignmentContext();
                          setOpenedClassroomId(null);
                          setActivePage("classrooms");
                          setSuccessMessage("Open a classroom to create its assignment.");
                          return;
                        }
                        setAssignmentForm({ ...emptyAssignmentForm, classroomId: classroom.id });
                        setIsCreatingAssignment(true);
                      }}
                      disabled={activeClassrooms.length === 0}
                      className="inline-flex items-center gap-1.5 sm:gap-2 rounded-full bg-[#137333] px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white shadow-xs transition hover:bg-[#0f5b28] disabled:cursor-not-allowed disabled:bg-gray-300 active:scale-[0.98] shrink-0"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Create</span>
                    </button>

                    {classrooms.length > 0 && (
                      <div className="flex items-center gap-1.5 min-w-0 flex-1 sm:flex-initial">
                        <label htmlFor="section-filter" className="text-xs font-medium text-[#5f6368] shrink-0">
                          Section:
                        </label>
                        <ValidatedSelect
                          id="section-filter"
                          value={assignmentFilterClassroomId}
                          onChange={(e) => { clearAssignmentContext(); setAssignmentFilterClassroomId(e.target.value); setOpenedClassroomId(e.target.value === "all" ? null : e.target.value); }}
                          className="h-9 w-full sm:w-auto max-w-[170px] sm:max-w-[220px] rounded-lg border border-[#dadce0] bg-white px-2.5 sm:px-3 text-xs font-medium text-[#202124] outline-none transition focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea] truncate"
                        >
                          <option value="all">All sections</option>
                          {classrooms.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name} — Section {c.section}
                            </option>
                          ))}
                        </ValidatedSelect>
                      </div>
                    )}
                  </div>
                </div>

              {filteredAssignments.length === 0 ? (
                <div className="rounded-xl border border-dashed border-[#dadce0] bg-white p-8 sm:p-12 text-center max-w-md mx-auto my-6 sm:my-8">
                  <div className="mx-auto flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-[#e6f4ea] text-[#137333]">
                    <ClipboardIcon className="h-6 w-6 sm:h-7 sm:w-7" />
                  </div>
                  <h3 className="mt-4 text-base sm:text-lg font-medium text-[#202124]">No assignments yet</h3>
                  <p className="mt-1 text-xs sm:text-sm text-[#5f6368]">
                    {activeClassrooms.length === 0
                      ? archivedClassrooms.length > 0
                        ? "All your classes are currently archived. Restore or create an active class before creating assignments."
                        : "Create a class first before creating assignments."
                      : "Click + Create to post your first assignment for this section."}
                  </p>
                  {activeClassrooms.length > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const classroom = activeClassrooms.find((c) => c.id === assignmentFilterClassroomId);
                        if (!classroom) {
                          clearAssignmentContext();
                          setOpenedClassroomId(null);
                          setActivePage("classrooms");
                          setSuccessMessage("Open a classroom to create its assignment.");
                          return;
                        }
                        setAssignmentForm({ ...emptyAssignmentForm, classroomId: classroom.id });
                        setIsCreatingAssignment(true);
                      }}
                      className="mt-4 sm:mt-5 inline-flex items-center gap-2 rounded-full bg-[#137333] px-5 py-2 text-xs sm:text-sm font-medium text-white hover:bg-[#0f5b28]"
                    >
                      <PlusIcon className="h-4 w-4" />
                      <span>Create assignment</span>
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <PaginatedList items={filteredAssignments} resetKey={assignmentFilterClassroomId} label="Assignments">{pageItems => pageItems.map((assignment) => (
                    <article
                      key={assignment.id}
                      onClick={() => setSelectedAssignmentId(assignment.id)}
                      className="group flex flex-col gap-3 rounded-2xl border border-[#dadce0] bg-white p-4 sm:p-5 transition-all hover:border-[#137333] hover:shadow-xs cursor-pointer"
                    >
                      {/* Card Top: Icon, Title, Section tag, and top-right Edit button */}
                      <div className="flex items-start justify-between gap-3 min-w-0">
                        <div className="flex items-start gap-3 sm:gap-3.5 min-w-0">
                          <div className="flex h-10 w-10 sm:h-11 sm:w-11 shrink-0 items-center justify-center rounded-full bg-[#e6f4ea] text-[#137333]">
                            <ClipboardIcon className="h-5 w-5" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="text-sm sm:text-base font-medium text-[#202124] group-hover:text-[#137333] group-hover:underline truncate">
                              {assignment.title}
                            </h3>
                            <div className="mt-1 flex flex-wrap items-center gap-1.5">
                              <span className="rounded bg-[#f1f3f4] px-2 py-0.5 text-[11px] sm:text-xs font-medium text-[#3c4043]">
                                {assignment.classroomName} • Section {assignment.classroomSection}
                              </span>
                            </div>
                            {assignment.instructions && (
                              <p className="mt-1.5 text-xs text-[#5f6368] line-clamp-1 max-w-xl">
                                {assignment.instructions}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Top-right Edit button on mobile */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditAssignment(assignment);
                          }}
                          className="sm:hidden shrink-0 rounded-full p-2 text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#202124]"
                          title="Edit assignment"
                          aria-label="Edit assignment"
                        >
                          <EditIcon className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Card Bottom: Due Date, Turned in Badge, and Student Work Action */}
                      <div
                        className="mt-1 pt-3 border-t border-[#f1f3f4] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between sm:justify-start gap-2.5 flex-wrap">
                          <span className="text-xs text-[#5f6368]">
                            Due {formatDateTime(assignment.dueDate)}
                          </span>

                          <span className="rounded-full bg-[#e6f4ea] px-2.5 py-0.5 text-xs font-semibold text-[#137333]">
                            {assignment.submissions} turned in
                          </span>
                        </div>

                        <div className="flex items-center gap-2 self-stretch sm:self-auto justify-end">
                          <button
                            type="button"
                            onClick={() => handleOpenEditAssignment(assignment)}
                            className="hidden sm:inline-flex rounded-full p-1.5 text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#202124]"
                            title="Edit assignment"
                            aria-label="Edit assignment"
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setSelectedAssignmentId(assignment.id)}
                            className="inline-flex w-full sm:w-auto items-center justify-center gap-1.5 rounded-full border border-[#dadce0] px-4 py-1.5 text-xs font-medium text-[#3c4043] transition hover:bg-[#f8f9fa] hover:border-[#137333] hover:text-[#137333]"
                          >
                            <span>Student work</span>
                            <span aria-hidden="true">→</span>
                          </button>
                        </div>
                      </div>
                    </article>
                  ))}</PaginatedList>
                </div>
              )}
            </div>
          )
        )}

          {activePage === "upload" && (
            <div>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-xs font-black uppercase tracking-wider text-[#137333]">
                      Manual check
                    </p>
                    <h2 className="mt-1 text-3xl sm:text-4xl font-black tracking-normal text-[#202124]">
                      Upload station
                    </h2>
                    <p className="mt-1 max-w-[680px] text-sm sm:text-base font-semibold leading-relaxed text-[#5f6368]">
                      Review student work from a photo, document, readable file, or pasted text.
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleResetManualCheck}
                    className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-[#dadce0] bg-white px-4 text-xs sm:text-sm font-semibold text-[#3c4043] transition hover:bg-[#f8f9fa] hover:text-[#202124] hover:border-[#c4c7c5] shadow-2xs active:scale-[0.98]"
                    title="Clear uploaded files and OCR text"
                  >
                    <svg className="h-4 w-4 text-[#5f6368]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    <span>Clear station</span>
                  </button>
                </div>

                {!selectedScanAssignment && <label className="block text-sm font-medium text-[#5f6368]" htmlFor="scan-assignment">
                  Link assignment
                  <select id="scan-assignment" value="all" onChange={event => setLinkedScanAssignmentId(event.target.value)}
                    className="mt-1 block w-full max-w-md rounded-lg border border-[#dadce0] bg-white px-3 py-2 text-sm text-[#202124]">
                    <option value="all">No assignment linked</option>
                    {assignments.map(assignment => <option key={assignment.id} value={assignment.id}>
                      {assignment.title} / {assignment.classroomName}
                    </option>)}
                  </select>
                </label>}

                {/* Linked Assignment Banner */}
                {selectedScanAssignment && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-[#c2e7ff] bg-[#f0f7ff] p-3.5 sm:px-4 shadow-2xs animate-fadeIn">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-[#1967d2] text-white shadow-2xs">
                        <ClipboardIcon className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="rounded bg-[#d3e3fd] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#041e49]">
                            Linked Assignment
                          </span>
                          <span className="text-xs font-medium text-[#5f6368]">
                            {selectedScanAssignment.classroomName} • Section {selectedScanAssignment.classroomSection || "Standard"}
                          </span>
                        </div>
                        <h3 className="mt-0.5 truncate text-sm sm:text-base font-bold text-[#202124]">
                          {selectedScanAssignment.title}
                        </h3>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap">
                      <div className="flex items-center gap-1.5 text-xs text-[#5f6368] bg-white border border-[#dadce0] px-3 py-1.5 rounded-lg shadow-2xs">
                        <span className="font-semibold text-[#202124]">
                          {selectedScanRoster.filter((r) => r.isSubmitted).length} / {selectedScanRoster.length}
                        </span>
                        <span>submitted</span>
                      </div>
                      {selectedScanAssignment.dueDate && (
                        <div className="text-xs text-[#5f6368] bg-white border border-[#dadce0] px-3 py-1.5 rounded-lg shadow-2xs">
                          <span>Due: {formatDateTime(selectedScanAssignment.dueDate)}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => setLinkedScanAssignmentId("all")}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-[#1967d2] hover:text-[#041e49] hover:bg-[#d3e3fd]/40 px-2.5 py-1.5 rounded-lg transition"
                      >
                        Unlink
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6 lg:min-h-[720px]">
                {loadedSubmission ? (
                  <SubmissionStation submission={loadedSubmission} onReview={handleOpenReview} onClear={handleResetManualCheck} />
                ) : manualCheckResult ? (
                  <div className="rounded-xl border border-emerald-200 bg-gradient-to-r from-emerald-50/80 to-teal-50/40 p-5 shadow-xs transition">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-center gap-3.5 min-w-0">
                        <div
                          className="relative grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-emerald-700 text-white shadow-sm overflow-hidden cursor-pointer group"
                          onClick={() => {
                            if (manualCheckImageUrl) setIsManualImageExpanded(true);
                          }}
                          title={manualCheckImageUrl ? "Click to expand image" : ""}
                        >
                          {manualCheckImageUrl ? (
                            <>
                              <img
                                src={manualCheckImageUrl}
                                alt="Submission thumbnail"
                                className="h-full w-full object-cover transition group-hover:scale-110"
                              />
                              <div className="absolute inset-0 bg-black/25 opacity-0 group-hover:opacity-100 transition flex items-center justify-center">
                                <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" /></svg>
                              </div>
                            </>
                          ) : uploadMode === "picture" ? (
                            <ImageIcon className="h-6 w-6" />
                          ) : (
                            <FileIcon className="h-6 w-6" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-black uppercase tracking-wider text-emerald-800">
                              Analyzed Submission
                            </span>
                            {hasManualImageExtraction && (
                              <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-extrabold text-emerald-800">
                                {transcriptionResult?.detectedLineCount || manualLiveOcrResult?.detectedLineCount || 26} lines OCR
                              </span>
                            )}
                            <span className="rounded bg-white px-2 py-0.5 text-xs font-bold text-gray-700 border border-emerald-100 shadow-2xs">
                              {manualCheckResult?.wordCount || (transcribedText || manualCheckText).split(/\s+/).filter(Boolean).length} words
                            </span>
                          </div>
                          <h4 className="truncate text-base font-black text-gray-950 mt-1">
                            {manualCheckTitle || manualCheckFiles[0]?.name || "Student Essay Submission"}
                          </h4>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5 shrink-0 flex-wrap">
                        {manualCheckImageUrl && (
                          <button
                            type="button"
                            onClick={() => setIsManualImageExpanded(true)}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3.5 py-2 text-xs font-extrabold text-emerald-800 hover:bg-emerald-50 transition shadow-2xs"
                            title="Open full view of the scanned essay image"
                          >
                            <ImageIcon className="h-3.5 w-3.5 text-emerald-700" />
                            <span>View image</span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setIsEditingTranscript((prev) => !prev)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-4 py-2 text-xs font-extrabold text-gray-700 hover:bg-gray-50 hover:text-gray-950 transition shadow-xs"
                        >
                          <span>{isEditingTranscript ? "Hide Editor" : "Edit & Re-scan"}</span>
                        </button>
                        <button
                          type="button"
                          onClick={handleResetManualCheck}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-4 py-2 text-xs font-extrabold text-white hover:bg-emerald-800 transition shadow-xs"
                        >
                          <PlusIcon className="h-4 w-4" />
                          <span>New Scan</span>
                        </button>
                      </div>
                    </div>

                    {isEditingTranscript && (
                      <ValidatedForm onSubmit={handleRunManualCheck} className="mt-4 border-t border-emerald-200/70 pt-4">
                        <label className="block text-xs font-black uppercase tracking-wider text-emerald-900 mb-2">
                          Edit transcript before re-scanning:
                        </label>
                        <ValidatedTextarea
                          value={uploadMode === "text" ? manualCheckText : transcribedText}
                          onChange={(e) => {
                            if (uploadMode === "text") {
                              setManualCheckText(e.target.value);
                            } else {
                              setTranscribedText(e.target.value);
                            }
                          }}
                          rows={6}
                          className="w-full rounded-lg border border-emerald-300 bg-white p-3 text-sm font-semibold leading-6 text-gray-900 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                        />
                        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                          <span className="text-xs font-medium text-gray-500">
                            Re-scanning will rerun Copyleaks plagiarism detection on the updated text.
                          </span>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setIsEditingTranscript(false)}
                              className="rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-xs font-extrabold text-gray-600 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="submit"
                              disabled={isScanningManualCheck}
                              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-700 px-4 py-2 text-xs font-extrabold text-white hover:bg-emerald-800 transition"
                            >
                              <FileSearchIcon className="h-4 w-4" />
                              <span>{isScanningManualCheck ? "Re-scanning..." : "Re-scan Plagiarism"}</span>
                            </button>
                          </div>
                        </div>
                      </ValidatedForm>
                    )}
                  </div>
                ) : (
                  <ValidatedForm
                    id="scan-station-form-top"
                    onSubmit={handleRunManualCheck}
                    className="flex min-h-[560px] flex-col rounded-lg border border-gray-200 bg-gray-50 p-4 sm:p-6"
                  >
                    {loadedSubmissionInfo && (
                      <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-emerald-950 shadow-2xs">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[#137333] text-white shadow-xs">
                            <CheckIcon className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[11px] font-bold uppercase tracking-wider text-[#137333]">
                                Auto-loaded Student Submission
                              </span>
                              <span className="rounded bg-[#e6f4ea] border border-[#ceead6] px-2 py-0.2 text-[10px] font-black text-[#137333]">
                                Active in station
                              </span>
                            </div>
                            <p className="text-sm font-bold text-[#202124] truncate">
                              {loadedSubmissionInfo.studentName} — {loadedSubmissionInfo.essayTitle}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            type="button"
                            onClick={() => {
                              handleResetManualCheck();
                            }}
                            className="inline-flex items-center gap-1 rounded-lg border border-[#dadce0] bg-white px-3 py-1.5 text-xs font-semibold text-[#3c4043] transition hover:bg-[#f1f3f4] hover:text-[#202124] shadow-2xs"
                          >
                            <span>Clear station</span>
                          </button>
                        </div>
                      </div>
                    )}

                    <div className="grid gap-3 md:grid-cols-3">
                    {uploadModes.map(({ id, label, icon: Icon }) => {
                      const isActive =
                        uploadMode === id;

                      return (
                        <button
                          key={id}
                          type="button"
                          disabled={isTranscribing || isScanningManualCheck}
                          onClick={() => {
                            manualSelection.current++;
                            setUploadMode(id);
                            setManualCheckFiles([]);
                            setTranscribedText("");
                            setTranscriptionResult(null);
                            setManualCheckResult(null);
                            setManualLiveOcrResult(null);
                            setManualCheckError("");
                          }}
                          className={
                            isActive
                              ? "flex min-h-[104px] flex-col items-center justify-center gap-3 rounded-lg border-2 border-emerald-700 bg-white px-4 text-sm font-extrabold text-emerald-800 shadow-sm"
                              : "flex min-h-[104px] flex-col items-center justify-center gap-3 rounded-lg border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-500 transition hover:border-emerald-300 hover:text-gray-950"
                          }
                        >
                          <span className={isActive ? "grid h-11 w-11 place-items-center rounded-lg bg-emerald-100 text-emerald-700" : "grid h-11 w-11 place-items-center rounded-lg bg-gray-100 text-gray-500"}>
                            <Icon className="h-5 w-5" />
                          </span>
                          {label}
                        </button>
                      );
                    })}
                  </div>

                  <label className="mt-6 block">
                    <span className="text-sm font-extrabold text-gray-800">
                      Check title
                    </span>
                    <ValidatedInput
                      type="text"
                      value={manualCheckTitle}
                      onChange={(event) => setManualCheckTitle(event.target.value)}
                      placeholder="Example: Grade 10 essay draft"
                      className="mt-2 h-11 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm font-semibold outline-none transition placeholder:text-gray-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                    />
                  </label>

                  <div className="mt-5 flex flex-1 flex-col">
                    {!uploadMode && (
                      <div className="grid flex-1 min-h-[320px] place-items-center rounded-lg border-2 border-dashed border-gray-300 bg-white px-6 text-center">
                        <div>
                          <span className="mx-auto grid h-16 w-16 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
                            <UploadIcon className="h-8 w-8" />
                          </span>
                          <h3 className="mt-5 text-xl font-black text-gray-950">
                            Choose a submission type first
                          </h3>
                          <p className="mt-2 max-w-[430px] text-sm font-semibold leading-6 text-gray-500">
                            Pick picture, file, or pasted text above to open the right submission box.
                          </p>
                        </div>
                      </div>
                    )}

                    {(uploadMode === "picture" || uploadMode === "file") && (
                      <>
                        {uploadMode === "picture" && (
                          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50/70 p-3.5">
                            <div className="flex items-center gap-3">
                              <span className="grid h-9 w-9 place-items-center rounded-lg bg-emerald-700 text-white shadow-sm">
                                <SparklesIcon className="h-5 w-5" />
                              </span>
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="text-sm font-extrabold text-emerald-950">
                                    AI Transcription Engine
                                  </h4>
                                  <span className="rounded bg-emerald-200/80 px-2 py-0.5 text-[10px] font-black uppercase text-emerald-900">
                                    Active
                                  </span>
                                </div>
                                <p className="text-xs font-semibold text-emerald-800">
                                  YOLO26x Line Detector (1024px Grayscale) + TrOCR Transformer (BEST_MODEL-TROCR)
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              {ocrEngineInfo?.device && (
                                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-bold text-emerald-800 shadow-sm">
                                  <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"></span>
                                  {ocrEngineInfo.device.toUpperCase()} Accelerated
                                </span>
                              )}
                              {manualCheckFiles.length === 0 && (
                                <button
                                  type="button"
                                  onClick={handleLoadSampleEssay}
                                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-xs font-extrabold text-emerald-800 shadow-sm transition hover:bg-emerald-100"
                                >
                                  <FileIcon className="h-3.5 w-3.5 text-emerald-600" />
                                  Try sample essay
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                        {uploadMode === "file" && manualCheckFiles.some(file => /\.doc$/i.test(file.name)) && <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-700 dark:bg-red-950 dark:text-red-200">{DOC_LIMITATION}</p>}
                        {uploadMode === "file" && manualCheckFiles.map((file) => (
                          <SubmissionFilePreview key={`${file.name}-${file.lastModified}`} file={file} />
                        ))}
                        {uploadMode === "file" && manualCheckFiles.length > 0 ? (
                          <div className="flex flex-1 flex-col justify-between rounded-xl border-2 border-emerald-300 bg-white p-5 sm:p-6 shadow-sm">
                            <div className="space-y-4">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100 pb-4">
                                <div className="flex items-center gap-3.5 min-w-0">
                                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-emerald-100 text-emerald-800 shadow-2xs border border-emerald-200">
                                    <FileIcon className="h-7 w-7" />
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="rounded-md bg-emerald-700 px-2.5 py-0.5 text-[11px] font-black uppercase tracking-wider text-white">
                                        {getFileKind(manualCheckFiles[0])}
                                      </span>
                                      <span className="text-xs font-semibold text-emerald-900 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                                        {formatFileSize(manualCheckFiles[0].size)}
                                      </span>
                                      {loadedSubmissionInfo && (
                                        <span className="inline-flex items-center gap-1 rounded-full bg-[#e6f4ea] px-2.5 py-0.5 text-[11px] font-bold text-[#137333]">
                                          <CheckIcon className="h-3 w-3 text-[#137333]" />
                                          Auto-loaded from {loadedSubmissionInfo.studentName}
                                        </span>
                                      )}
                                    </div>
                                    <h4 className="mt-1 text-base sm:text-lg font-black text-gray-950 truncate max-w-lg">
                                      {manualCheckFiles[0].name}
                                    </h4>
                                    <p className="text-xs text-[#5f6368] truncate">
                                      {loadedSubmissionInfo
                                        ? `Submission for ${loadedSubmissionInfo.assignmentTitle || "Assignment"} • Ready to scan`
                                        : "Attached file ready for plagiarism and originality verification"}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 shrink-0">
                                  <label className="cursor-pointer inline-flex items-center gap-1.5 rounded-lg border border-[#dadce0] bg-white px-3 py-1.5 text-xs font-semibold text-[#3c4043] hover:bg-[#f1f3f4] transition shadow-2xs">
                                    <span>Change file</span>
                                    <ValidatedInput
                                      type="file"
                                      accept={ACCEPTED_CHECK_FILE_TYPES}
                                      multiple
                                      disabled={isTranscribing || isScanningManualCheck}
                              onChange={handleManualCheckFiles}
                                      className="sr-only"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    disabled={isTranscribing || isScanningManualCheck}
                                    onClick={() => {
                                      changeManualPages([]);
                                      setManualCheckText("");
                                    }}
                                    className="rounded-lg border border-[#dadce0] bg-white px-3 py-1.5 text-xs font-semibold text-[#c5221f] hover:bg-red-50 hover:border-red-200 transition shadow-2xs"
                                  >
                                    Remove
                                  </button>
                                </div>
                              </div>

                              {manualCheckText ? (
                                <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-4">
                                  <div className="flex items-center justify-between border-b border-emerald-100 pb-2 mb-2">
                                    <span className="text-xs font-black uppercase tracking-wider text-emerald-900">
                                      Loaded Document Text Content
                                    </span>
                                    <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-800">
                                      {manualCheckText.split(/\s+/).filter(Boolean).length} words
                                    </span>
                                  </div>
                                  <div className="max-h-48 overflow-y-auto font-mono text-xs leading-relaxed text-gray-800 whitespace-pre-wrap pr-1 bg-white p-3 rounded-lg border border-emerald-100">
                                    {manualCheckText.slice(0, 1200)}
                                    {manualCheckText.length > 1200 ? "..." : ""}
                                  </div>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 text-xs font-semibold text-emerald-900">
                                  <SparklesIcon className="h-5 w-5 text-emerald-700 shrink-0" />
                                  <span>The original document is shown above. Its text will be extracted when you run this manual check.</span>
                                </div>
                              )}
                            </div>

                            <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3 text-xs text-[#5f6368]">
                              <span className="inline-flex items-center gap-1 font-medium">
                                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                Submission loaded automatically — no upload required
                              </span>
                              <span className="font-semibold text-emerald-800">Ready to scan</span>
                            </div>
                          </div>
                        ) : (
                          <label className="flex flex-1 min-h-[340px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-white px-6 text-center transition hover:border-emerald-600 hover:bg-emerald-50">
                            {manualImagePreview ? (
                              <div className="flex w-full flex-col items-center py-4">
                                {loadedSubmissionInfo && (
                                  <div className="mb-3 inline-flex items-center gap-1 rounded-full bg-[#e6f4ea] px-3 py-1 text-xs font-bold text-[#137333]">
                                    <CheckIcon className="h-3.5 w-3.5 text-[#137333]" />
                                    Auto-loaded from {loadedSubmissionInfo.studentName}'s handwriting
                                  </div>
                                )}
                                <img
                                  src={manualImagePreview}
                                  alt="Manual check preview"
                                  className="max-h-[300px] w-full rounded-lg object-contain"
                                />
                                <p className="mt-3 text-xs font-bold text-gray-500">
                                  Click or drop another image to replace
                                </p>
                              </div>
                            ) : (
                              <>
                                <span className="grid h-20 w-20 place-items-center rounded-lg bg-emerald-100 text-emerald-700">
                                  {uploadMode === "picture" ? (
                                    <ImageIcon className="h-10 w-10" />
                                  ) : (
                                    <UploadIcon className="h-10 w-10" />
                                  )}
                                </span>
                                <span className="mt-6 text-2xl font-black text-gray-950">
                                  {uploadMode === "picture" ? "Upload student handwriting" : "Upload a file"}
                                </span>
                                <span className="mt-2 max-w-[520px] text-sm font-semibold leading-6 text-gray-500">
                                  {uploadMode === "picture"
                                    ? "Select a PNG, JPG, JPEG, or WEBP photo of handwritten student work to transcribe."
                                    : "Upload a PDF or Microsoft Word document (.pdf, .doc, .docx)."}
                                </span>
                              </>
                            )}
                            <ValidatedInput
                              type="file"
                              accept={
                                uploadMode === "picture"
                                  ? "image/png,image/jpeg,image/jpg,image/webp"
                                  : ACCEPTED_CHECK_FILE_TYPES
                              }
                              multiple
                              disabled={isTranscribing || isScanningManualCheck}
                              onChange={handleManualCheckFiles}
                              className="sr-only"
                            />
                          </label>
                        )}

                        {manualCheckFiles.length > 0 && manualCheckFiles.every(isEssayImage) && <EssayPagePicker
                          files={manualCheckFiles} onChange={changeManualPages} onError={setManualCheckError}
                          disabled={isTranscribing || isScanningManualCheck} />}
                        {manualLiveOcrResult?.pageCount && isTranscribing && <p role="status" className="mt-3 font-semibold">
                          Page {manualLiveOcrResult.pageNumber} of {manualLiveOcrResult.pageCount} ? {manualLiveOcrResult.phase}
                        </p>}
                        {transcriptionResult?.pages?.length > 1 && <details open className="mt-4"><summary>Page transcriptions</summary>
                          {transcriptionResult.pages.map((page, index) => <label key={page.page_number} className="mt-2 block">Page {page.page_number}
                            <ValidatedTextarea aria-label={`Page ${page.page_number} transcription`} value={page.text} rows={5}
                              disabled={isTranscribing || isScanningManualCheck} className="block w-full rounded border p-3"
                              onChange={event => {
                                const pages = transcriptionResult.pages.map((old, i) => i === index ? { ...old, text: event.target.value } : old);
                                const text = pages.some(p => !p.text.trim()) ? "" : combinePageText(pages);
                                setTranscriptionResult({ ...transcriptionResult, pages, text }); setTranscribedText(text); setManualCheckResult(null);
                              }} />
                          </label>)}
                          <h4 className="mt-3 font-semibold">Complete Essay</h4><pre className="whitespace-pre-wrap">{transcribedText}</pre>
                        </details>}
                        {uploadMode === "picture" && manualCheckFiles.length > 0 && (
                          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white p-3.5 shadow-sm">
                            <button
                              type="button"
                              disabled={isTranscribing || isScanningManualCheck}
                              onClick={() => handleTranscribePicture()}
                              className="inline-flex h-11 items-center gap-2 rounded-lg bg-emerald-700 px-5 text-sm font-black text-white shadow-sm transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-emerald-300"
                            >
                              <SparklesIcon className="h-4 w-4" />
                              {isTranscribing
                                ? `Transcribing with YOLO + TrOCR (${manualLiveOcrResult?.processedLineCount || 0}/${manualLiveOcrResult?.detectedLineCount || "?"} lines)...`
                                : transcribedText
                                  ? "Re-transcribe handwriting"
                                  : "Transcribe handwriting"}
                            </button>

                            {transcribedText && (
                              <button
                                type="button"
                                onClick={() => handleCopyTranscript()}
                                className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 text-sm font-extrabold text-gray-700 transition hover:bg-gray-100 hover:text-gray-950"
                              >
                                {copySuccess ? (
                                  <>
                                    <CheckIcon className="h-4 w-4 text-emerald-600" />
                                    <span className="text-emerald-700">Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <CopyIcon className="h-4 w-4 text-gray-500" />
                                    <span>Copy transcript</span>
                                  </>
                                )}
                              </button>
                            )}

                            {transcribedText && (
                              <button
                                type="button"
                                onClick={handleTransferToText}
                                className="inline-flex h-11 items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-extrabold text-gray-700 transition hover:bg-gray-50 hover:text-gray-950"
                              >
                                <ClipboardIcon className="h-4 w-4 text-gray-500" />
                                Edit in Paste tab
                              </button>
                            )}

                            <button
                              type="button"
                              disabled={isTranscribing || isScanningManualCheck}
                              onClick={() => {
                                changeManualPages([]);
                                setManualImagePreview("");
                                setTranscribedText("");
                                setTranscriptionResult(null);
                                setManualLiveOcrResult(null);
                                setShowLineBreakdown(false);
                              }}
                              className="ml-auto text-xs font-extrabold text-gray-500 hover:text-red-600 transition"
                            >
                              Remove image
                            </button>
                          </div>
                        )}

                        {uploadMode === "picture" && (isTranscribing || transcribedText || manualLiveOcrResult?.text) && (
                          <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-emerald-100 pb-3">
                              <div className="flex items-center gap-2">
                                <span className="grid h-7 w-7 place-items-center rounded-md bg-emerald-100 text-emerald-700">
                                  <SparklesIcon className="h-4 w-4" />
                                </span>
                                <h4 className="text-sm font-black text-gray-950">
                                  Transcribed Handwriting
                                </h4>
                                <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-extrabold text-emerald-800">
                                  {transcriptionResult?.detectedLineCount || manualLiveOcrResult?.detectedLineCount || 0} lines detected
                                </span>
                                {transcribedText && (
                                  <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-bold text-gray-600">
                                    {transcribedText.split(/\s+/).filter(Boolean).length} words
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setShowLineBreakdown((prev) => !prev)}
                                  className="inline-flex items-center gap-1 text-xs font-extrabold text-emerald-700 hover:text-emerald-900"
                                >
                                  <span>{showLineBreakdown ? "Hide line boxes" : "Show line breakdown"}</span>
                                  <ChevronDownIcon className={`h-3.5 w-3.5 transition-transform ${showLineBreakdown ? "rotate-180" : ""}`} />
                                </button>
                              </div>
                            </div>

                            {isTranscribing && (
                              <div className="mt-3 space-y-1.5">
                                <div className="flex justify-between text-xs font-bold text-emerald-800">
                                  <span>
                                    {manualLiveOcrResult?.detectedLineCount
                                      ? `Transcribing line crops with TrOCR (${manualLiveOcrResult.processedLineCount}/${manualLiveOcrResult.detectedLineCount})...`
                                      : "Detecting handwriting line boxes with YOLO26x..."}
                                  </span>
                                  <span>
                                    {manualLiveOcrResult?.detectedLineCount
                                      ? `${Math.round(((manualLiveOcrResult.processedLineCount || 0) / manualLiveOcrResult.detectedLineCount) * 100)}%`
                                      : "Processing..."}
                                  </span>
                                </div>
                                <div className="h-2 w-full overflow-hidden rounded-full bg-emerald-200">
                                  <div
                                    className="h-full bg-emerald-600 transition-all duration-300"
                                    style={{
                                      width: manualLiveOcrResult?.detectedLineCount
                                        ? `${Math.max(8, Math.round(((manualLiveOcrResult.processedLineCount || 0) / manualLiveOcrResult.detectedLineCount) * 100))}%`
                                        : "20%",
                                    }}
                                  />
                                </div>
                              </div>
                            )}

                            <div className="mt-3">
                              <label className="block text-xs font-bold text-gray-500 mb-1">
                                {isTranscribing ? "Live streaming recognition:" : "Editable transcript (refine words before scanning if needed):"}
                              </label>
                              <ValidatedTextarea
                                readOnly={manualCheckFiles.length > 1}
                                disabled={isTranscribing || isScanningManualCheck}
                                value={transcribedText || manualLiveOcrText}
                                onChange={(e) => setTranscribedText(e.target.value)}
                                rows={Math.min(10, Math.max(5, (transcribedText || manualLiveOcrText).split("\n").length + 1))}
                                placeholder="Transcribed handwritten essay text will appear here as YOLO and TrOCR process the image..."
                                className="w-full rounded-lg border border-emerald-200 bg-white p-3 text-sm font-semibold leading-6 text-gray-900 outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                              />
                            </div>

                            {showLineBreakdown && (transcriptionResult?.lines?.length > 0 || manualLiveOcrResult?.lines?.length > 0) && (
                              <div className="mt-4 border-t border-emerald-100 pt-3">
                                <h5 className="text-xs font-black uppercase tracking-wider text-emerald-900 mb-2">
                                  Line-by-Line Detection & Transcription
                                </h5>
                                <div className="max-h-60 overflow-y-auto space-y-1.5 pr-1">
                                  {(transcriptionResult?.lines || manualLiveOcrResult?.lines || []).map((line, idx) => {
                                    const box = (transcriptionResult?.boxes || manualLiveOcrResult?.boxes || [])[idx];
                                    return (
                                      <div key={idx} className="flex items-start gap-2 rounded bg-white p-2 text-xs border border-emerald-100">
                                        <span className="font-mono font-black text-emerald-700 min-w-[28px]">
                                          #{idx + 1}
                                        </span>
                                        <span className="flex-1 font-semibold text-gray-800">
                                          {line}
                                        </span>
                                        {box && (
                                          <span className="font-mono text-[10px] text-gray-400">
                                            [{Math.round(box.x1)}, {Math.round(box.y1)}, {Math.round(box.x2)}, {Math.round(box.y2)}]
                                          </span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {manualCheckFiles.length > 0 && uploadMode !== "file" && (
                          <div className="mt-5 rounded-lg border border-gray-200 bg-white">
                            <div className="wc-file-heading grid grid-cols-[1fr_auto_auto] gap-3 border-b border-gray-100 px-4 py-3 text-xs font-extrabold uppercase tracking-normal text-gray-500">
                              <span>File</span>
                              <span>Type</span>
                              <span>Size</span>
                            </div>

                            {manualCheckFiles.map((file) => (
                              <div
                                key={`${file.name}-${file.size}-${file.lastModified}`}
                                className="wc-file-row grid grid-cols-[1fr_auto_auto] gap-3 border-b border-gray-100 px-4 py-3 text-sm last:border-b-0"
                              >
                                <span data-label="File" className="min-w-0 truncate font-extrabold text-gray-950">
                                  {file.name}
                                </span>
                                <span data-label="Type" className="font-bold text-gray-500">
                                  {getFileKind(file)}
                                </span>
                                <span data-label="Size" className="font-bold text-gray-500">
                                  {formatFileSize(file.size)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}

                    {uploadMode === "text" && (
                      <label className="flex flex-1 flex-col">
                        <span className="text-sm font-extrabold text-gray-800">
                          Paste text
                        </span>
                        <ValidatedTextarea
                          value={manualCheckText}
                          onChange={(event) => {
                            setManualCheckText(event.target.value);
                            setManualCheckResult(null);
                            setManualLiveOcrResult(null);
                          }}
                          placeholder="Paste essay text, copied paragraphs, or OCR output here."
                          className="mt-2 flex-1 min-h-[360px] w-full rounded-lg border border-gray-300 bg-white px-4 py-4 text-sm font-semibold leading-6 outline-none transition placeholder:text-gray-400 focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                        />
                      </label>
                    )}
                  </div>

                  {manualCheckError && (
                    <p role="alert" className="mt-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-950 dark:text-red-200">
                      {manualCheckError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={isScanningManualCheck || isTranscribing || !uploadMode}
                    className="mt-6 inline-flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 px-5 text-base font-extrabold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-emerald-300"
                  >
                    <FileSearchIcon className="h-5 w-5" />
                    {isScanningManualCheck
                      ? "Scanning submission..."
                      : isTranscribing
                        ? "Transcribing handwriting..."
                        : transcribedText
                          ? "Scan transcribed essay for plagiarism"
                          : "Scan for plagiarism"}
                  </button>
                </ValidatedForm>
                )}

                {(isScanningManualCheck || manualCheckResult) && (
                <section className="mt-6 rounded-lg border border-gray-200 bg-white p-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-extrabold uppercase tracking-normal text-emerald-700">
                        Detection result
                      </p>
                      <h3 className="mt-2 text-2xl font-black">
                        {isScanningManualCheck
                          ? "Scanning submission"
                          : manualCheckResult?.title || "Ready to scan"}
                      </h3>
                    </div>

                    {manualCheckResult && (
                      <span className={`rounded-lg px-3 py-2 text-sm font-black ${manualResultBadgeClass}`}>
                        {manualCheckResult.label}
                      </span>
                    )}
                  </div>

                  {isScanningManualCheck ? (
                    <div className="mt-8 rounded-lg border border-emerald-100 bg-emerald-50 px-5 py-5">
                      {isTranscribing || (!plagiarismScanProgressText && manualLiveOcrResult) ? (
                        <>
                          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                            <div>
                              <p className="text-sm font-extrabold text-emerald-800">
                                Detecting text...
                              </p>
                              <p className="mt-2 text-sm font-semibold leading-6 text-emerald-900">
                                YOLO has detected the line crops. TrOCR text appears here as each batch finishes.
                              </p>
                            </div>

                            {manualLiveOcrResult && (
                              <span className="rounded-lg bg-white px-3 py-2 text-xs font-black text-emerald-800">
                                {manualLiveOcrResult.processedLineCount}/{manualLiveOcrResult.detectedLineCount} lines
                              </span>
                            )}
                          </div>

                          <pre className="mt-4 max-h-[320px] overflow-auto whitespace-pre-wrap rounded-lg border border-emerald-100 bg-white px-4 py-3 text-sm font-semibold leading-6 text-gray-800">
                            {manualLiveOcrText || "Waiting for the first recognized line..."}
                          </pre>
                        </>
                      ) : (
                        <div className="flex items-center gap-3 py-3">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-700 border-t-transparent shrink-0" />
                          <div>
                            <p className="text-sm font-extrabold text-emerald-950">
                              {plagiarismScanProgressText || "Scanning submission with Copyleaks..."}
                            </p>
                            <p className="text-xs font-semibold text-emerald-700 mt-1">
                              Connecting securely to Copyleaks Authenticity API & database matching.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : manualCheckResult ? (
                    <>
                      {/* 1. Score Box & 2x2 Stats Grid FIRST */}
                      <div className="mt-7 grid gap-4 sm:grid-cols-[160px_1fr]">
                        <div className={`grid aspect-square place-items-center rounded-lg bg-white text-center ring-8 ${manualResultRingClass}`}>
                          <div>
                            <strong className="block text-5xl font-black">
                              {manualCheckResult.score}%
                            </strong>
                            <span className="mt-1 block text-xs font-extrabold uppercase tracking-normal text-gray-500">
                              Plagiarism score
                            </span>
                          </div>
                        </div>

                        <div className="grid gap-3 sm:grid-cols-2">
                          <div className="rounded-lg bg-gray-50 p-4">
                            <p className="text-2xl font-black text-gray-950">
                              {manualCheckResult.wordCount}
                            </p>
                            <p className="mt-1 text-sm font-bold text-gray-500">
                              Total words
                            </p>
                          </div>

                          <div className="rounded-lg bg-gray-50 p-4">
                            <p className="text-2xl font-black text-gray-950">
                              {manualCheckResult.identicalWords ?? 0}
                            </p>
                            <p className="mt-1 text-sm font-bold text-gray-500">
                              Matched / identical words
                            </p>
                          </div>

                          <div className="rounded-lg bg-gray-50 p-4">
                            <p className="text-2xl font-black text-emerald-800">
                              {manualCheckResult.scanStatus || "Completed"}
                            </p>
                            <p className="mt-1 text-sm font-bold text-gray-500">
                              Scan status
                            </p>
                          </div>

                          <div className="rounded-lg bg-gray-50 p-4">
                            <p className="text-2xl font-black text-gray-950">
                              {manualCheckResult.matchedSources?.length ?? 0}
                            </p>
                            <p className="mt-1 text-sm font-bold text-gray-500">
                              Matching sources
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* 2. Original Scanned Image & Transcribed Handwriting Section */}
                      <div className="mt-7">
                        {manualCheckImageUrl ? (
                          <div className="grid gap-6 lg:grid-cols-2">
                            {/* Left Column: Original Scanned Image */}
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between pb-3">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-black text-gray-900">
                                    Original essay image
                                  </p>
                                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-extrabold text-emerald-800">
                                    Handwriting photo
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setIsManualImageExpanded(true)}
                                  className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-700 hover:text-emerald-900 transition"
                                >
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l-5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                                  <span>Click to expand</span>
                                </button>
                              </div>

                              <div
                                className="group relative flex flex-1 min-h-[360px] max-h-[520px] cursor-zoom-in items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-gray-50/80 shadow-xs transition hover:border-emerald-500 hover:bg-gray-100/50"
                                onClick={() => setIsManualImageExpanded(true)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => e.key === "Enter" && setIsManualImageExpanded(true)}
                                title="Click to view full screen"
                              >
                                <img
                                  src={manualCheckImageUrl}
                                  alt="Original essay submission"
                                  className="max-h-[500px] w-full object-contain p-2 transition duration-200 group-hover:scale-[1.01]"
                                />
                                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-lg bg-black/60 px-3 py-1.5 text-xs font-bold text-white opacity-0 backdrop-blur-xs transition-opacity group-hover:opacity-100">
                                  <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l-5-5m11 5l-5-5m5 5v-4m0 4h-4" /></svg>
                                  Expand
                                </div>
                              </div>
                            </div>

                            {/* Right Column: Transcribed Text & Plagiarism Highlights */}
                            <div className="flex flex-col">
                              <div className="flex items-center justify-between pb-3">
                                <div className="flex items-center gap-2">
                                  <p className="text-sm font-black text-gray-900">
                                    Transcribed student handwriting
                                  </p>
                                  {hasManualImageExtraction && (
                                    <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-extrabold text-emerald-800">
                                      YOLO26x + TrOCR
                                    </span>
                                  )}
                                </div>
                                {(manualDetectedText || transcribedText || manualCheckText) && (
                                  <button
                                    type="button"
                                    onClick={() => handleCopyTranscript(manualDetectedText || transcribedText || manualCheckText)}
                                    className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-700 hover:text-emerald-900 transition"
                                  >
                                    {copySuccess ? (
                                      <>
                                        <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
                                        <span>Copied!</span>
                                      </>
                                    ) : (
                                      <>
                                        <CopyIcon className="h-3.5 w-3.5" />
                                        <span>Copy text</span>
                                      </>
                                    )}
                                  </button>
                                )}
                              </div>
                              {manualImageExtractionSummary.length > 0 && (
                                <div className="mb-3 space-y-2">
                                  {manualImageExtractionSummary.map((summary) => (
                                    <p
                                      key={summary}
                                      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800"
                                    >
                                      {summary}
                                    </p>
                                  ))}
                                </div>
                              )}
                              <div className="flex-1">
                                <HighlightedText text={manualDetectedText || transcribedText || manualCheckText} scanResult={manualCheckResult} />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <div className="flex items-center justify-between pb-3">
                              <div className="flex items-center gap-2">
                                <p className="text-sm font-black text-gray-900">
                                  {hasManualImageExtraction ? "Transcribed student handwriting" : "Analyzed essay text"}
                                </p>
                                {hasManualImageExtraction && (
                                  <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-extrabold text-emerald-800">
                                    YOLO26x + TrOCR
                                  </span>
                                )}
                              </div>
                              {(manualDetectedText || transcribedText || manualCheckText) && (
                                <button
                                  type="button"
                                  onClick={() => handleCopyTranscript(manualDetectedText || transcribedText || manualCheckText)}
                                  className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-700 hover:text-emerald-900 transition"
                                >
                                  {copySuccess ? (
                                    <>
                                      <CheckIcon className="h-3.5 w-3.5 text-emerald-600" />
                                      <span>Copied!</span>
                                    </>
                                  ) : (
                                    <>
                                      <CopyIcon className="h-3.5 w-3.5" />
                                      <span>Copy text</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                            {manualImageExtractionSummary.length > 0 && (
                              <div className="mb-3 space-y-2">
                                {manualImageExtractionSummary.map((summary) => (
                                  <p
                                    key={summary}
                                    className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800"
                                  >
                                    {summary}
                                  </p>
                                ))}
                              </div>
                            )}
                            <HighlightedText text={manualDetectedText || transcribedText || manualCheckText} scanResult={manualCheckResult} />
                          </div>
                        )}
                      </div>

                      {manualCheckResult.matchedSources && manualCheckResult.matchedSources.length > 0 && (
                        <div className="mt-6">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-extrabold text-gray-800">
                              Matching sources ({manualCheckResult.matchedSources.length})
                            </p>
                            <span className="rounded bg-emerald-100 px-2 py-0.5 text-[11px] font-extrabold text-emerald-800">
                              Copyleaks Database
                            </span>
                          </div>
                          <div className="mt-3 space-y-2">
                            <PaginatedList items={manualCheckResult.matchedSources} resetKey={manualCheckResult.scanId} label="Matched sources">{pageItems => pageItems.map((source, sIdx) => (
                              <div
                                key={source.id || sIdx}
                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-lg border border-gray-200 bg-white p-3 text-sm"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-extrabold text-gray-900 truncate">
                                      {source.title || "Matched source"}
                                    </p>
                                    <span className="shrink-0 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700">
                                      {source.source_type || "Copyleaks Database"}
                                    </span>
                                  </div>
                                  {source.url && (
                                    <a
                                      href={source.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-xs font-semibold text-emerald-700 hover:underline truncate block mt-0.5"
                                    >
                                      {source.url}
                                    </a>
                                  )}
                                </div>
                                <div className="shrink-0">
                                  <span className="rounded bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-700">
                                    {source.matched_words || source.identical_words || 0} matched words
                                  </span>
                                </div>
                              </div>
                            ))}</PaginatedList>
                          </div>
                        </div>
                      )}

                      <p className="mt-6 text-sm font-semibold leading-6 text-gray-600">
                        {manualCheckResult.summary}
                      </p>

                      <div className="mt-6">
                        <p className="text-sm font-extrabold text-gray-800">
                          Review signals
                        </p>
                        <div className="mt-3 space-y-2">
                          {manualCheckResult.flags.map((flag) => (
                            <p
                              key={flag}
                              className="rounded-lg border border-gray-200 px-4 py-3 text-sm font-semibold text-gray-600"
                            >
                              {flag}
                            </p>
                          ))}
                        </div>
                      </div>

                      {manualCheckResult.repeatedPhrases.length > 0 && (
                        <div className="mt-6">
                          <p className="text-sm font-extrabold text-gray-800">
                            Repeated phrases
                          </p>
                          <div className="mt-3 space-y-2">
                            {manualCheckResult.repeatedPhrases.map((item) => (
                              <p
                                key={item.phrase}
                                className="rounded-lg bg-gray-50 px-4 py-3 text-sm font-semibold leading-6 text-gray-600"
                              >
                                "{item.phrase}" appears {item.count} times
                              </p>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  ) : null}
                </section>
                )}
              </div>

              {/* Linked Assignment Submissions Roster */}
              {selectedScanAssignment && (
                <div className="mt-8 rounded-2xl border border-[#dadce0] bg-white p-4 sm:p-6 shadow-2xs animate-fadeIn">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-[#dadce0] pb-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="grid h-8 w-8 place-items-center rounded-lg bg-[#e6f4ea] text-[#137333]">
                          <UsersIcon className="h-4 w-4" />
                        </span>
                        <h3 className="text-base sm:text-lg font-bold text-[#202124]">
                          Student Submissions & Roster
                        </h3>
                      </div>
                      <p className="mt-1 text-xs sm:text-sm text-[#5f6368]">
                        Enrolled students in {selectedScanAssignment.classroomName} • Section {selectedScanAssignment.classroomSection || "Standard"}
                      </p>
                    </div>

                    {/* Stats pills */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="rounded-full bg-[#f1f3f4] px-3 py-1 text-xs font-semibold text-[#3c4043]">
                        {selectedScanRoster.length} students
                      </span>
                      <span className="rounded-full bg-[#e8f0fe] px-3 py-1 text-xs font-semibold text-[#1967d2]">
                        {selectedScanRoster.filter((r) => r.isSubmitted && !r.isGraded).length} turned in
                      </span>
                      <span className="rounded-full bg-[#e6f4ea] px-3 py-1 text-xs font-semibold text-[#137333]">
                        {selectedScanRoster.filter((r) => r.isGraded).length} graded
                      </span>
                      {selectedScanRoster.filter((r) => !r.isSubmitted).length > 0 && (
                        <span className="rounded-full bg-[#fef7e0] px-3 py-1 text-xs font-semibold text-[#b06000]">
                          {selectedScanRoster.filter((r) => !r.isSubmitted).length} missing
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Roster table / list */}
                  <div className="mt-4 divide-y divide-[#f1f3f4] overflow-hidden rounded-xl border border-[#dadce0]">
                    {selectedScanRoster.length === 0 ? (
                      <div className="p-8 text-center text-xs sm:text-sm text-[#5f6368]">
                        No students in this assignment yet.
                      </div>
                    ) : (
                      <PaginatedList items={selectedScanRoster} resetKey={[selectedScanAssignment?.id]} label="Scan submissions">{pageItems => pageItems.map((item) => {
                        const initials = item.studentName
                          .split(" ")
                          .map((n) => n[0])
                          .filter(Boolean)
                          .slice(0, 2)
                          .join("")
                          .toUpperCase() || "S";

                        return (
                          <div
                            key={item.studentId}
                            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3.5 hover:bg-[#f8f9fa] transition"
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#e8f0fe] text-xs font-bold text-[#1967d2]">
                                {initials}
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-semibold text-[#202124] truncate">
                                    {item.studentName}
                                  </p>
                                  {item.isGraded ? (
                                    <span className="rounded-full bg-[#e6f4ea] px-2 py-0.5 text-[10px] font-bold text-[#137333]">
                                      Graded: {item.grade}/100
                                    </span>
                                  ) : item.isSubmitted ? (
                                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                      item.status === "late"
                                        ? "bg-[#fef7e0] text-[#b06000]"
                                        : "bg-[#e8f0fe] text-[#1967d2]"
                                    }`}>
                                      {item.status === "late" ? "Turned in late" : "Turned in"}
                                    </span>
                                  ) : (
                                    <span className="rounded-full bg-[#f1f3f4] px-2 py-0.5 text-[10px] font-medium text-[#5f6368]">
                                      Missing
                                    </span>
                                  )}
                                  {item.isSubmitted && <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-800">{processingLabel(item.submission?.processingState)}</span>}
                                  {item.isSubmitted && <SimilarityPreview submission={item.submission} submissions={submissions} onReview={handleOpenReview} />}

                                </div>
                                <p className="text-xs text-[#5f6368] truncate">
                                  {item.studentEmail || (item.essayTitle ? `Essay: ${item.essayTitle}` : "No email")}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              {item.isSubmitted ? (
                                <>
                                  <button
                                    type="button"
                                    disabled={loadingSubmissionId === item.submission?.id}
                                    onClick={() => handleLoadSubmissionToScanStation(item.submission)}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#dadce0] bg-white px-2.5 py-1.5 text-xs font-semibold text-[#3c4043] transition hover:bg-[#f1f3f4] hover:text-[#202124] shadow-2xs disabled:opacity-60"
                                    title="View original submission and automatic plagiarism result"
                                  >
                                    {loadingSubmissionId === item.submission?.id ? (
                                      <>
                                        <span className="h-3.5 w-3.5 border-2 border-[#137333] border-t-transparent rounded-full animate-spin shrink-0" />
                                        <span>Loading...</span>
                                      </>
                                    ) : (
                                      <>
                                        <UploadIcon className="h-3.5 w-3.5 text-[#5f6368]" />
                                        <span>View submission & result</span>
                                      </>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleOpenReview(item.submission)}
                                    className="inline-flex items-center gap-1 rounded-lg bg-[#137333] px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-[#0f5b28] shadow-2xs"
                                  >
                                    <EditIcon className="h-3.5 w-3.5" />
                                    <span>{item.isGraded ? "Edit Grade" : "Review & Grade"}</span>
                                  </button>
                                </>
                              ) : (
                                <span className="text-xs font-medium text-[#80868b] italic">
                                  Awaiting submission
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}</PaginatedList>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activePage === "submissions" && (
            <div className="space-y-6">
              <div className="flex items-start justify-between gap-3">
                <div><h2 className="text-xl sm:text-2xl font-medium tracking-tight text-[#202124]">Grades & Submissions</h2>
                <p className="mt-1 text-sm text-[#5f6368]">Review student work and grades.</p></div>
                <button type="button" className="classroom-icon" aria-label="Close grades" title="Close grades" onClick={() => {
                  clearAssignmentContext();
                  setOpenedClassroomId(classrooms.some(c => c.id === gradesOrigin) ? gradesOrigin : null);
                  setActivePage("classrooms");
                }}><CloseIcon className="h-5 w-5" /></button>
              </div>

              <div aria-label="Grade filters and actions" className="flex flex-wrap items-end gap-4 rounded-2xl border border-[#dadce0] bg-white p-4 shadow-2xs">
                <label htmlFor="filter-classroom" className="min-w-0 text-xs font-medium text-[#5f6368]">
                  Classroom:
                  <ValidatedSelect id="filter-classroom" value={selectedSubmissionsClassroomId}
                    onChange={event => selectGradeClassroom(event.target.value)}
                    className="mt-1 block h-10 max-w-full rounded-lg border border-[#dadce0] bg-[#fafafa] px-3 text-sm text-[#202124] outline-none focus:border-[#137333]">
                    <option value="all">All classrooms</option>
                    {classrooms.map(classroom => <option key={classroom.id} value={classroom.id}>
                      {classroom.name}{classroom.section ? ` / Section ${classroom.section}` : ""}
                    </option>)}
                  </ValidatedSelect>
                </label>
                <label htmlFor="filter-classwork" className="min-w-0 text-xs font-medium text-[#5f6368]">
                  Assignment:
                  <ValidatedSelect id="filter-classwork" value={selectedAssignment?.id || "all"}
                    onChange={event => selectGradeAssignment(event.target.value)}
                    className="mt-1 block h-10 max-w-full rounded-lg border border-[#dadce0] bg-[#fafafa] px-3 text-sm text-[#202124] outline-none focus:border-[#137333]">
                    <option value="all">All assignments</option>
                    {gradeClasswork.map(assignment => <option key={assignment.id} value={assignment.id}>
                      {assignment.title}{selectedSubmissionsClassroomId === "all" ? ` / ${assignment.classroomName}` : ""}
                    </option>)}
                  </ValidatedSelect>
                </label>
                {selectedSubmissionsClassroomId !== "all" && <button type="button" onClick={() => selectGradeClassroom("all")}
                  className="h-10 rounded-full px-3 text-xs font-medium text-[#137333] hover:bg-[#e6f4ea]">Reset filters</button>}
                <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                  {selectedAssignment && <>
                    <button type="button" onClick={() => { clearAssignmentContext(); setActivePage("assignments"); }}
                      className="rounded-lg border border-[#dadce0] px-3 py-2 text-xs font-medium text-[#3c4043] hover:bg-[#f8f9fa]"><ArrowLeftIcon className="action-icon" />Back to Classwork</button>
                    <button type="button" onClick={() => handleOpenEditAssignment(selectedAssignment)}
                      className="rounded-lg border border-[#dadce0] px-3 py-2 text-xs font-medium text-[#3c4043] hover:bg-[#f8f9fa]">Edit assignment</button>
                  </>}
                  <button type="button" disabled={isLoading || exportingGrades}
                    onClick={() => handleExportGrades(selectedAssignment
                      ? { assignmentId: selectedAssignment.id, classroomId: selectedAssignment.classroomId }
                      : { classroomId: selectedSubmissionsClassroomId !== "all" ? selectedSubmissionsClassroomId : openedClassroomId || undefined })}
                    className="rounded-lg bg-[#137333] px-4 py-2 text-sm font-medium text-white disabled:opacity-50">
                    {exportingGrades ? "Exporting..." : "Export grades (.xlsx)"}
                  </button>
                </div>
              </div>

              {selectedAssignment ? (
                renderAssignmentDetailsAndSubmissions(undefined, true)
              ) : (
                <div className="space-y-6">
                  {assignments.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-[#dadce0] bg-white p-8 sm:p-12 text-center max-w-md mx-auto my-6">
                      <div className="mx-auto flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-[#e6f4ea] text-[#137333]">
                        <ClipboardIcon className="h-6 w-6 sm:h-7 sm:w-7" />
                      </div>
                      <h3 className="mt-4 text-base sm:text-lg font-medium text-[#202124]">No assignments found</h3>
                      <p className="mt-1 text-xs sm:text-sm text-[#5f6368]">
                        Create an assignment first to begin tracking student submissions and assigning grades.
                      </p>
                      <button
                        type="button"
                        onClick={() => setActivePage("assignments")}
                        className="mt-4 sm:mt-5 inline-flex items-center gap-2 rounded-full bg-[#137333] px-5 py-2 text-xs sm:text-sm font-medium text-white hover:bg-[#0f5b28] transition"
                      >
                        <PlusIcon className="h-4 w-4" />
                        <span>Create assignment</span>
                      </button>
                    </div>
                  ) : submissionHubGroups.size === 0 ? (
                    <div className="rounded-2xl border border-dashed border-[#dadce0] bg-white p-10 text-center max-w-md mx-auto my-6">
                      <p className="text-sm font-medium text-[#202124]">No assignments match these filters.</p>
                      <p className="mt-1 text-xs text-[#5f6368]">Try selecting another classroom or assignment.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      <PaginatedList items={Array.from(submissionHubGroups.values())} resetKey={[selectedSubmissionsClassroomId, selectedAssignmentId]} label="Grade classrooms">{pageItems => pageItems.map((group) => (
                        <section key={group.id} className="rounded-2xl border border-[#dadce0] bg-white overflow-hidden shadow-2xs">
                          {/* Group Header Strip */}
                          <div className="flex items-center justify-between border-b border-[#dadce0] bg-[#f8f9fa] px-4 py-3 sm:px-5">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#137333] text-white shadow-2xs">
                                <ClipboardIcon className="h-4 w-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h3 className="text-sm sm:text-base font-semibold text-[#202124] truncate">
                                    {group.name}
                                  </h3>
                                  <span className="rounded-full bg-white border border-[#dadce0] px-2.5 py-0.5 text-[11px] font-medium text-[#3c4043]">
                                    Section {group.section || "Standard"}
                                  </span>
                                  {group.subject && group.subject !== "No subject" && group.subject !== group.name && (
                                    <span className="text-xs text-[#5f6368] hidden sm:inline">
                                      • {group.subject}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            <span className="rounded-full bg-[#f1f3f4] px-2.5 py-1 text-xs font-medium text-[#5f6368] shrink-0">
                              {group.assignments.length} {group.assignments.length === 1 ? "assignment" : "assignments"}
                            </span>
                          </div>

                          {/* Assignment Cards inside the Group */}
                          <div className="divide-y divide-[#f1f3f4] p-2 sm:p-3">
                            <PaginatedList items={group.assignments} resetKey={[selectedSubmissionsClassroomId, selectedAssignmentId, group.id]} label="Grade assignments">{pageItems => pageItems.map((assignment) => (
                              <article
                                key={assignment.id}
                                className="group flex flex-col gap-3 rounded-xl p-3 sm:p-4 transition hover:bg-[#f8f9fa] cursor-pointer sm:flex-row sm:items-center sm:justify-between"
                                onClick={() => selectGradeAssignment(assignment.id)}
                              >
                                <div className="flex items-start gap-3 sm:gap-3.5 min-w-0">
                                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e6f4ea] text-[#137333] mt-0.5">
                                    <ClipboardIcon className="h-5 w-5" />
                                  </div>
                                  <div className="min-w-0">
                                    <h4 className="text-sm sm:text-base font-medium text-[#202124] group-hover:text-[#137333] group-hover:underline truncate">
                                      {assignment.title}
                                    </h4>
                                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-[#5f6368]">
                                      <span>Due {formatDateTime(assignment.dueDate)}</span>
                                      {assignment.instructions && (
                                        <>
                                          <span aria-hidden="true">•</span>
                                          <span className="truncate max-w-xs">{assignment.instructions}</span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div
                                  className="flex items-center justify-between sm:justify-end gap-3 shrink-0 pt-2 sm:pt-0 border-t border-[#f1f3f4] sm:border-t-0"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="rounded-full bg-[#e6f4ea] px-2.5 py-0.5 text-xs font-semibold text-[#137333]">
                                      {assignment.submissionStats.submitted} turned in
                                    </span>
                                    <span className="rounded-full bg-[#f1f3f4] px-2 py-0.5 text-xs font-medium text-[#5f6368]">
                                      {assignment.submissionStats.missing} missing
                                    </span>
                                  </div>

                                  <button
                                    type="button"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setAssignmentDetailTab("roster");
                                      selectGradeAssignment(assignment.id);
                                    }}
                                    className="inline-flex items-center gap-1.5 rounded-full bg-[#137333] hover:bg-[#0f5b28] text-white px-3.5 py-1.5 text-xs font-medium transition shadow-2xs active:scale-[0.98]"
                                  >
                                    <span>Review work</span>
                                    <span aria-hidden="true">→</span>
                                  </button>
                                </div>
                              </article>
                            ))}</PaginatedList>
                          </div>
                        </section>
                      ))}</PaginatedList>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {peerInspection && <SimilarityDialog title={peerInspection.matchedId ? "Peer Similarity Review" : "Peer Similarity Matches"}
            onClose={() => setPeerInspection(null)}>
            <div hidden={Boolean(peerInspection.matchedId)}>
              <ClassmateMatches submission={peerInspection.submission}
                onInspect={matchedId => setPeerInspection(current => ({ ...current, matchedId }))} />
            </div>
            {peerInspection.matchedId && <>
              <button type="button" className="mb-4 text-sm text-[#137333] underline"
                onClick={() => setPeerInspection(current => ({ ...current, matchedId: null }))}><ArrowLeftIcon className="action-icon" />Back to matches</button>
              <PeerComparison key={peerInspection.matchedId} submission={peerInspection.submission} matchedSubmissionId={peerInspection.matchedId} />
            </>}
          </SimilarityDialog>}

          {/* Edit Assignment Modal Dialog */}
          {editingAssignment && (
            <div className="wc-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-gray-950/60 p-4 backdrop-blur-sm animate-in fade-in duration-150">
              <div className="wc-modal-panel relative max-h-[92vh] w-full max-w-lg overflow-y-auto overflow-x-hidden rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
                <div className="wc-modal-heading sticky -top-6 z-10 flex items-start justify-between gap-4 border-b border-gray-100 bg-white py-4">
                  <div>
                    <span className="inline-block rounded-md bg-emerald-100 px-2.5 py-0.5 text-xs font-black text-emerald-800 uppercase tracking-wider">
                      Edit Assignment
                    </span>
                    <h3 className="mt-1.5 text-2xl font-black text-gray-950">
                      Update details
                    </h3>
                  </div>
                  <button
                    type="button"
                    onClick={handleCloseEditAssignment}
                    className="rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-700 text-lg font-bold"
                  >
                    ✕
                  </button>
                </div>

                <ValidatedForm onSubmit={handleUpdateAssignment} className="mt-5 space-y-4">
                  <p className="text-sm text-gray-600">{classrooms.find((c) => c.id === editingAssignment.classroomId)?.name}</p>

                  <label className="block">
                    <span className="text-xs font-extrabold uppercase tracking-wider text-gray-700">
                      Title
                    </span>
                    <ValidatedInput
                      type="text"
                      value={editingAssignment.title}
                      onChange={(e) =>
                        setEditingAssignment((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      className="mt-1.5 h-11 w-full rounded-lg border border-gray-300 px-3 text-sm font-semibold outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                      required
                    />
                  </label>

                  <label className="block">
                    <span className="text-xs font-extrabold uppercase tracking-wider text-gray-700">
                      Instructions
                    </span>
                    <ValidatedTextarea
                      value={editingAssignment.instructions}
                      onChange={(e) =>
                        setEditingAssignment((prev) => ({
                          ...prev,
                          instructions: e.target.value,
                        }))
                      }
                      rows="4"
                      className="mt-1.5 w-full rounded-lg border border-gray-300 p-3 text-sm font-semibold outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                    />
                  </label>

                  <label className="block">
                    <span className="text-xs font-extrabold uppercase tracking-wider text-gray-700">
                      Due date
                    </span>
                    <ValidatedInput
                      type="datetime-local"
                      value={editingAssignment.dueDate}
                      onChange={(e) =>
                        setEditingAssignment((prev) => ({
                          ...prev,
                          dueDate: e.target.value,
                        }))
                      }
                      className="mt-1.5 h-11 w-full rounded-lg border border-gray-300 px-3 text-sm font-semibold outline-none transition focus:border-emerald-600 focus:ring-4 focus:ring-emerald-100"
                    />
                  </label>

                  <div className="mt-4 rounded-xl border border-[#dadce0] bg-[#f8f9fa] p-3.5">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <ValidatedInput
                        type="checkbox"
                        checked={editingAssignment.acceptLateSubmissions !== false}
                        onChange={(e) =>
                          setEditingAssignment((prev) => ({
                            ...prev,
                            acceptLateSubmissions: e.target.checked,
                          }))
                        }
                        className="mt-0.5 h-4 w-4 rounded border-[#dadce0] text-[#137333] focus:ring-[#137333]"
                      />
                      <div>
                        <span className="text-sm font-medium text-[#202124]">Allow late submissions</span>
                        <p className="mt-0.5 text-xs text-[#5f6368]">
                          If checked, students can turn in work after the deadline (marked as Late). If unchecked, late submissions are blocked once overdue.
                        </p>
                      </div>
                    </label>
                  </div>

                  <div className="mt-6 flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handleCloseEditAssignment}
                      className="h-11 rounded-lg border border-gray-300 bg-white px-4 text-sm font-extrabold text-gray-700 transition hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isUpdatingAssignment}
                      className="inline-flex h-11 items-center gap-2 rounded-lg bg-emerald-700 px-5 text-sm font-extrabold text-white transition hover:bg-emerald-800 disabled:bg-emerald-400 shadow-sm"
                    >
                      {isUpdatingAssignment ? "Saving..." : "Save changes"}
                    </button>
                  </div>
                </ValidatedForm>
              </div>
            </div>
          )}

          {deletingAssignment && <DeleteAssignmentDialog key={deletingAssignment.id} assignment={deletingAssignment}
            onClose={() => setDeletingAssignment(null)} onDeleted={handleAssignmentDeleted} />}

          {reviewingSubmission && <SubmissionReview key={reviewingSubmission.id} ocrEngineInfo={ocrEngineInfo}
            submission={reviewingSubmission} submissions={submissions} profile={profile}
            text={reviewTranscribedText}
            result={reviewScanResult} scanning={isReviewScanning} progress={reviewScanProgressText}
            onRecheck={handleRunReviewScan} onClose={handleCloseReview} saving={isSavingGrade}
            onCopy={handleCopyReviewTranscript} copied={reviewCopySuccess}
            error={errorMessage || submissionSyncError} message={successMessage}
            imageUrl={reviewImagePreviewUrl} imageLoading={isLoadingImagePreview}
            imageExpanded={isImageExpanded} onExpandImage={setIsImageExpanded}
            grading={
                  <ValidatedForm onSubmit={handleSaveGrade} className="mt-3 rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <h4 className="text-base font-black text-emerald-950">
                      Grading
                    </h4>
                    <p className="mt-1 text-xs font-semibold text-emerald-800">
                      Results stay private until returned. Editing saved results makes them private again.
                    </p>

                    <div className="mt-4">
                      <label htmlFor="review-grade" className="block text-xs font-extrabold uppercase tracking-wider text-gray-700">
                        Grade / Score
                      </label>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <ValidatedInput
                          type="text"
                          id="review-grade"
                          disabled={isSavingGrade}
                          placeholder="e.g. 95 or A+"
                          value={gradeInput}
                          onChange={(e) => setGradeInput(e.target.value)}
                          className="h-11 w-44 rounded-lg border border-gray-300 bg-white px-3.5 text-base font-black text-gray-900 placeholder:text-gray-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                        />
                        <div className="flex flex-wrap gap-1.5">
                          {[100, 95, 90, 85, 80, 75].map((score) => (
                            <button
                              key={score}
                              type="button"
                              disabled={isSavingGrade}
                              onClick={() => setGradeInput(String(score))}
                              className={`h-9 rounded-lg border px-3 text-xs font-bold transition ${
                                gradeInput === String(score)
                                  ? "border-emerald-600 bg-emerald-700 text-white"
                                  : "border-gray-200 bg-white text-gray-700 hover:bg-emerald-50 hover:text-emerald-800"
                              }`}
                            >
                              {score}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <label htmlFor="review-feedback" className="block text-xs font-extrabold uppercase tracking-wider text-gray-700">
                        Teacher Feedback (optional)
                      </label>
                      <ValidatedTextarea
                        id="review-feedback"
                        disabled={isSavingGrade}
                        rows={5}
                        placeholder="Leave remarks or suggestions for the student..."
                        value={feedbackInput}
                        onChange={(e) => setFeedbackInput(e.target.value)}
                        className="mt-2 w-full rounded-lg border border-gray-300 bg-white p-3 text-sm font-semibold text-gray-800 placeholder:text-gray-400 focus:border-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                      />
                    </div>

                    <p className="mt-3 text-sm font-medium text-emerald-800">
                      {reviewingSubmission.returnedAt ? "Returned to student" : "Grade hidden until you return work"}
                    </p>
                    <RevisionHistory history={reviewingSubmission.revisionHistory} />
                    {reviewingSubmission.revisionRequest && <p className="mt-3 text-sm text-amber-800">Revision requested: {reviewingSubmission.revisionRequest.message}</p>}
                    <div className="mt-5 flex flex-wrap justify-end gap-3">
                      <button type="button" onClick={handleRequestRevision}
                        disabled={isSavingGrade || isReviewScanning || Boolean(reviewingSubmission.revisionRequest) || ["submitted", "processing"].includes(reviewingSubmission.processingState)}
                        className="rounded-lg border border-amber-400 bg-amber-50 px-4 py-2.5 text-xs font-bold text-amber-900 disabled:opacity-50">Request revision</button>
                      <button type="button" onClick={handleReturnWork}
                        disabled={isSavingGrade || Boolean(reviewingSubmission.revisionRequest) || isReviewScanning || ["submitted", "processing", "failed"].includes(reviewingSubmission.processingState) || !String(gradeInput ?? "").trim()}
                        className="rounded-lg bg-blue-700 px-4 py-2.5 text-xs font-extrabold text-white hover:bg-blue-800 disabled:opacity-50">
                        <ArrowLeftIcon className="action-icon" />Return work
                      </button>
                      <button
                        type="button"
                        onClick={handleCloseReview}
                        disabled={isSavingGrade || isReviewScanning}
                        className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-xs font-extrabold text-gray-700 transition hover:bg-gray-50"
                      >
                        Close
                      </button>
                      <button
                        type="submit"
                        disabled={isSavingGrade || Boolean(reviewingSubmission.revisionRequest)}
                        className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-5 py-2.5 text-xs font-extrabold text-white transition hover:bg-emerald-800 disabled:bg-emerald-400 shadow-sm"
                      >
                        {isSavingGrade ? "Saving grade..." : <><CheckIcon className="action-icon" />Save Grade</>}
                      </button>
                    </div>
                  </ValidatedForm>
            }
          />}
          {/* Lightbox for Upload station manual check image */}
          {isManualImageExpanded && manualCheckImageUrl && (
            <div
              className="wc-modal-backdrop fixed inset-0 z-[80] flex items-center justify-center bg-black/85 p-4 backdrop-blur-sm animate-in fade-in duration-150"
              onClick={() => setIsManualImageExpanded(false)}
            >
              <div className="wc-modal-panel relative max-h-[95vh] max-w-[95vw]">
                <button
                  type="button"
                  onClick={() => setIsManualImageExpanded(false)}
                  className="absolute -right-3 -top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white font-bold text-gray-800 shadow-xl hover:bg-gray-100 text-sm transition"
                  title="Close full view"
                >
                  ✕
                </button>
                <img
                  src={manualCheckImageUrl}
                  alt="Original essay full view"
                  className="max-h-[92vh] max-w-[92vw] rounded-xl object-contain shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                />
                <p className="mt-2 text-center text-xs font-semibold text-white/80">
                  {manualCheckTitle || manualCheckFiles[0]?.name || "Original Scanned Essay"}
                </p>
              </div>
            </div>
          )}
        {/* Archive / Restore Confirmation Modal Dialog */}
        {archivingClassroom && (
          <div className="wc-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
            <div className="wc-modal-panel w-full max-w-md rounded-2xl border border-[#dadce0] bg-white p-6 shadow-xl">
              <div className="flex items-center gap-3">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${
                    archivingClassroom.archive
                      ? "bg-amber-100 text-amber-800"
                      : "bg-[#e6f4ea] text-[#137333]"
                  }`}
                >
                  {archivingClassroom.archive ? (
                    <ArchiveIcon className="h-6 w-6" />
                  ) : (
                    <UnarchiveIcon className="h-6 w-6" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#202124]">
                    {archivingClassroom.archive ? "Archive class?" : "Restore class?"}
                  </h3>
                  <p className="text-xs text-[#5f6368] font-medium">
                    {archivingClassroom.name}
                  </p>
                </div>
              </div>

              <p className="mt-4 text-sm text-[#5f6368] leading-relaxed">
                {archivingClassroom.archive
                  ? `Archiving "${archivingClassroom.name}" moves it to your Archived classes tab. The class becomes read-only: all past assignments, submissions, and grades remain safely preserved, but no new assignments or submissions can be posted. You can restore this class at any time.`
                  : `Restoring "${archivingClassroom.name}" moves it back to your Active classes list. You and enrolled students will be able to post assignments and submit work again.`}
              </p>

              <div className="mt-6 flex items-center justify-end gap-3 pt-4 border-t border-[#dadce0]">
                <button
                  type="button"
                  disabled={isArchiving}
                  onClick={() => setArchivingClassroom(null)}
                  className="rounded-full px-4 py-2 text-sm font-medium text-[#5f6368] hover:bg-[#f1f3f4] transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isArchiving}
                  onClick={() =>
                    handleArchiveClassroom(
                      archivingClassroom.id,
                      archivingClassroom.archive
                    )
                  }
                  className={`inline-flex items-center gap-1.5 rounded-full px-5 py-2 text-sm font-medium text-white shadow-xs transition disabled:opacity-50 ${
                    archivingClassroom.archive
                      ? "bg-amber-600 hover:bg-amber-700 active:bg-amber-800"
                      : "bg-[#137333] hover:bg-[#0f5b28] active:bg-[#0c471f]"
                  }`}
                >
                  {isArchiving ? (
                    <span>Processing...</span>
                  ) : archivingClassroom.archive ? (
                    <>
                      <ArchiveIcon className="h-4 w-4" />
                      <span>Archive</span>
                    </>
                  ) : (
                    <>
                      <UnarchiveIcon className="h-4 w-4" />
                      <span>Restore</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Copy Class Modal (Google Classroom Clone Feature) */}
        {copyingClassroom && (
          <div className="wc-modal-backdrop fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4 animate-in fade-in duration-150">
            <div className="wc-modal-panel w-full max-w-md rounded-2xl border border-[#dadce0] bg-white p-6 shadow-xl">
              <div className="wc-modal-heading flex items-center justify-between pb-3 border-b border-[#dadce0]">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#e6f4ea] text-[#137333]">
                    <CopyIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-[#202124]">Copy class</h3>
                    <p className="text-xs text-[#5f6368]">Duplicate syllabus and coursework</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setCopyingClassroom(null)}
                  disabled={isCopying}
                  className="rounded-full p-1 text-[#5f6368] hover:bg-[#f1f3f4] transition"
                >
                  ✕
                </button>
              </div>

              <ValidatedForm onSubmit={handleConfirmCopyClassroom} className="mt-4 space-y-4">
                <div className="rounded-xl bg-[#f8f9fa] border border-[#dadce0] p-3 text-xs text-[#5f6368] space-y-1">
                  <p className="font-semibold text-[#202124]">What gets copied:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-[11px] text-[#444746]">
                    <li>All assignment titles, instructions, and settings</li>
                    <li>A brand new classroom join code</li>
                  </ul>
                  <p className="font-semibold text-[#202124] pt-1">What does NOT get copied:</p>
                  <ul className="list-disc list-inside space-y-0.5 text-[11px] text-[#444746]">
                    <li>Previous students and class rosters</li>
                    <li>Past essay submissions and grades</li>
                  </ul>
                </div>

                <div>
                  <label htmlFor="copy-class-name" className="block text-xs font-semibold uppercase tracking-wider text-[#444746] mb-1">
                    Class name (required)
                  </label>
                  <ValidatedInput
                    id="copy-class-name"
                    required
                    value={copyForm.name}
                    onChange={(e) => setCopyForm((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. Copy of History 101"
                    className="h-10 w-full rounded-xl border border-[#dadce0] bg-[#fafafa] px-3 text-sm text-[#202124] outline-none transition focus:bg-white focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label htmlFor="copy-section" className="block text-xs font-semibold uppercase tracking-wider text-[#444746] mb-1">
                      Section (required)
                    </label>
                    <ValidatedInput
                      id="copy-section"
                      required
                      value={copyForm.section}
                      onChange={(e) => setCopyForm((prev) => ({ ...prev, section: e.target.value }))}
                      placeholder="e.g. 2A, Fall 2026"
                      className="h-10 w-full rounded-xl border border-[#dadce0] bg-[#fafafa] px-3 text-sm text-[#202124] outline-none transition focus:bg-white focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                    />
                  </div>
                  <div>
                    <label htmlFor="copy-subject" className="block text-xs font-semibold uppercase tracking-wider text-[#444746] mb-1">
                      Subject
                    </label>
                    <ValidatedInput
                      id="copy-subject"
                      value={copyForm.subject}
                      onChange={(e) => setCopyForm((prev) => ({ ...prev, subject: e.target.value }))}
                      placeholder="e.g. History"
                      className="h-10 w-full rounded-xl border border-[#dadce0] bg-[#fafafa] px-3 text-sm text-[#202124] outline-none transition focus:bg-white focus:border-[#137333] focus:ring-2 focus:ring-[#e6f4ea]"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#dadce0]">
                  <button
                    type="button"
                    disabled={isCopying}
                    onClick={() => setCopyingClassroom(null)}
                    className="rounded-full px-4 py-2 text-sm font-medium text-[#5f6368] hover:bg-[#f1f3f4] transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isCopying}
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#137333] px-5 py-2 text-sm font-medium text-white shadow-xs transition hover:bg-[#0f5b28] disabled:opacity-50"
                  >
                    {isCopying ? "Copying class..." : "Copy class"}
                  </button>
                </div>
              </ValidatedForm>
            </div>
          </div>
        )}
        </section>
      </main>
    </div>
  );
}
